import { SceneFilters } from "@/components/scene-filters"
import { useCallback, useEffect, useRef, useState } from "react"
import type { PointerEvent as ReactPointerEvent } from "react"
import { createPortal } from "react-dom"
import { cn } from "cn"
import { Link, createFileRoute } from "@tanstack/react-router"
import {
  AlertCircle,
  Check,
  ChevronDown,
  Download,
  Ellipsis,
  Pause,
  Play,
  RectangleHorizontal,
  RectangleVertical,
  RotateCcw,
  Save,
  Sparkles,
  Trash2,
  Volume2,
  VolumeX,
  ZoomIn,
  ZoomOut,
} from "lucide-react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button, buttonVariants } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLegend,
  FieldSet,
  FieldLabel,
} from "@/components/ui/field"
import { ReframingEditor } from "@/components/reframing-editor"
import { FramingPreview } from "@/components/framing-preview"
import {
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from "@/components/ui/popover"
import { ButtonGroup } from "@/components/ui/button-group"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Slider } from "@/components/ui/slider"
import { Separator } from "@/components/ui/separator"
import { Spinner } from "@/components/ui/spinner"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { ClipTimeline } from "@/components/clip-timeline"
import { CaptionsEditor } from "@/components/captions-editor"
import { TikTokConnectDialog } from "@/components/app/tiktok-connect-dialog"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import { CaptionPresetCarousel } from "@/components/caption-preset-carousel"
import { CaptionPreview } from "@/components/caption-preview"
import { EditorMediaLibrary } from "@/components/editor-media-library"
import {
  sameRanges,
  sourceTimeAtTimelineTime,
  splitRange,
  trimRange,
  rangeDuration,
  timelineTimeAtSourceTime,
} from "@/lib/clip-cuts"
import type { ClipRange } from "@/lib/clip-cuts"
import {
  automaticCaptionY,
  captionPresetOptions,
  activeCaptionWordIndex,
  captionWordTextRange,
  normalizeCaptionPresetName,
  captionFontFamilies,
  captionFixedFontWeights,
  clipResolution,
  captionPositionSegmentAt,
  positionCaptionRange,
  defaultCaptionPositionSegments,
  SUBTITLE_DEFAULT_MAX_WORDS,
  SUBTITLE_PRESENTATION_LEAD_SECONDS,
  SUBTITLE_SAFE_EDGE_PADDING_PX,
  captionsWithDrafts,
  cuesForRanges,
  fitPreviewCues,
  mergedCaptionOverrides,
  normalizeImages,
  normalizeSounds,
  retainedCaptionOverrides,
  samePosition,
  formatTime,
  editorDirtyState,
  applyCaptionTimingOverrides,
  applySceneBoundaries,
  effectiveEditorScenes,
} from "@/lib/clip-editor-model"
import type {
  CaptionPresetOption,
  SavedCaptionPreset,
  CaptionAppearance,
  CaptionOverride,
  CaptionPositionSegment,
  CaptionTimingOverride,
  ClipFormat,
  ClipExportResolution,
  EditorDraft,
  EditorData,
  EditorImage,
  EditorSound,
  FacecamOverride,
  SceneFramingOverrides,
  PreviewCaptionCue,
  SubtitlePosition,
  SubtitlePreset,
} from "@/lib/clip-editor-model"
import { toast } from "sonner"
import { useEditorDirtyState } from "@/hooks/use-editor-dirty-state"
import { useClipEditorLoader } from "@/hooks/use-clip-editor-loader"
import { useSoundPlaybackSync } from "@/hooks/use-sound-playback-sync"
import { useClipEditorSave } from "@/hooks/use-clip-editor-save"
import { useCaptionEdits } from "@/hooks/use-caption-edits"
import { useVideoPreviewSync } from "@/hooks/use-video-preview-sync"
import { useEditorCutActions } from "@/hooks/use-editor-cut-actions"
import type { EditorUndoEntry } from "@/hooks/use-editor-cut-actions"
import { useClipAudioMedia } from "@/hooks/use-clip-audio-media"
import { useClipImageMedia } from "@/hooks/use-clip-image-media"
import { usePreviewImageInteractions } from "@/hooks/use-preview-image-interactions"
import { useCaptionPositionInteractions } from "@/hooks/use-caption-position-interactions"
import { videoClient } from "@/lib/video-client"

export const Route = createFileRoute(
  "/app/videos/$videoId_/clips/$clipId/edit"
)({
  component: ClipEditorPage,
})

function ShortPreview({ src, startSeconds }: { src: string; startSeconds: number }) {
  return (
    <video
      className="size-8 rounded-sm object-cover"
      muted
      playsInline
      preload="metadata"
      src={src}
      onLoadedMetadata={(event) => {
        event.currentTarget.currentTime = startSeconds
      }}
      aria-hidden="true"
    />
  )
}

function ClipEditorPage() {
  const { videoId, clipId } = Route.useParams()
  const [sceneRequestPending, setSceneRequestPending] = useState(false)
  const [sceneRequestError, setSceneRequestError] = useState<string | null>(null)
  const [selectedFormat, setSelectedFormat] = useState<ClipFormat>("9:16")
  const [selectedExportResolution, setSelectedExportResolution] = useState<ClipExportResolution>(1080)
  const [subtitlesEnabled, setSubtitlesEnabled] = useState(true)
  const [selectedPreset, setSelectedPreset] = useState<SubtitlePreset | null>(
    null
  )
  const [newSavedPresets, setNewSavedPresets] = useState<SavedCaptionPreset[]>([])
  const [selectedSavedPresetId, setSelectedSavedPresetId] = useState<string | null>(null)
  const [savePresetOpen, setSavePresetOpen] = useState(false)
  const [presetName, setPresetName] = useState("")
  const [presetSaving, setPresetSaving] = useState(false)
  const [presetError, setPresetError] = useState<string | null>(null)
  const [appearance, setAppearance] = useState<CaptionAppearance>({
    textCase: null, fontFamily: null, textColor: null, accentColor: null,
    bold: null, italic: null, maxWords: SUBTITLE_DEFAULT_MAX_WORDS,
  })
  const [, refreshCaptionFontMetrics] = useState(0)
  const fixedFontWeight = appearance.fontFamily
    ? captionFixedFontWeights[appearance.fontFamily]
    : undefined
  useEffect(() => {
    if (!appearance.fontFamily || fixedFontWeight === undefined) return
    let cancelled = false
    void document.fonts.load(`${fixedFontWeight} 48px "Intoshort Caption ${appearance.fontFamily}"`).then(
      () => { if (!cancelled) refreshCaptionFontMetrics((version) => version + 1) },
      () => { if (!cancelled) setError("The caption font could not be loaded. Please reload the editor.") }
    )
    return () => { cancelled = true }
  }, [appearance.fontFamily, fixedFontWeight])
  const [selectedFontSize, setSelectedFontSize] = useState<number | null>(null)
  const [selectedMarginHorizontal, setSelectedMarginHorizontal] = useState<
    number | null
  >(null)
  const [selectedPosition, setSelectedPosition] =
    useState<SubtitlePosition | null>(null)
  const [sceneColorFilters, setSceneColorFilters] = useState<EditorDraft["sceneColorFilters"]>([])
  const [sceneFramingOverrides, setSceneFramingOverrides] = useState<SceneFramingOverrides>(null)
  const [facecamOverride, setFacecamOverride] =
    useState<FacecamOverride>(null)
  const facecamDragRef = useRef<{
    pointerId: number
    clientX: number
    clientY: number
    rect: NonNullable<FacecamOverride>
  } | null>(null)
  const [captionPositionSegments, setCaptionPositionSegments] = useState<
    CaptionPositionSegment[]
  >([])
  const positionedCaptionRef = useRef<PreviewCaptionCue | undefined>(undefined)
  const [, setSelectedCaptionPositionSegment] =
    useState(0)
  const [error, setError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  const editorRouteRef = useRef(`${videoId}/${clipId}`)
  const [currentTime, setCurrentTime] = useState(0)
  const [videoPaused, setVideoPaused] = useState(true)
  const [videoMuted, setVideoMuted] = useState(false)
  const [videoVolume, setVideoVolume] = useState(100)
  const snapshotCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const [cutRanges, setCutRanges] = useState<ClipRange[]>([])
  const [undoHistory, setUndoHistory] = useState<EditorUndoEntry[]>([])
  const [selectedRange, setSelectedRange] = useState<number | null>(null)
  const [sounds, setSounds] = useState<EditorSound[]>([])
  const [images, setImages] = useState<EditorImage[]>([])
  const soundsRef = useRef<EditorSound[]>([])
  const imagesRef = useRef<EditorImage[]>([])
  soundsRef.current = sounds
  imagesRef.current = images
  const [captionOverrides, setCaptionOverrides] = useState<CaptionOverride[]>(
    []
  )
  const [captionTimingOverrides, setCaptionTimingOverrides] = useState<CaptionTimingOverride[]>([])
  const [selectedCaptionIdentity, setSelectedCaptionIdentity] = useState<string | null>(null)
  const [selectedSoundId, setSelectedSoundId] = useState<string | null>(null)
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null)
  const [mediaClipboard, setMediaClipboard] = useState<
    | { kind: "sound"; snapshot: EditorSound }
    | { kind: "image"; snapshot: EditorImage }
    | null
  >(null)
  const [splitActive, setSplitActive] = useState(false)
  const [activeWorkspace, setActiveWorkspace] = useState("video")
  const [captionTab, setCaptionTab] = useState("text")
  const [captionFocusRequest, setCaptionFocusRequest] = useState<{ cueKey: string } | undefined>()
  const [timelineZoom, setTimelineZoom] = useState(1)
  useEffect(() => {
    setTimelineZoom(1)
  }, [clipId])
  const [timelineHost, setTimelineHost] = useState<HTMLElement | null>(null)
  const navigate = Route.useNavigate()
  const [shorts, setShorts] = useState<Array<{ id: string; title: string; partNumber: number; startSeconds: number; removed: boolean }>>([])
  const [shortsLoading, setShortsLoading] = useState(false)
  const [shortsError, setShortsError] = useState(false)
  const [shortsMenuOpen, setShortsMenuOpen] = useState(false)
  const [switchTarget, setSwitchTarget] = useState<string | null>(null)
  const dirtyRef = useEditorDirtyState()
  const draftVersionRef = useRef(0)
  const appliedSceneSegmentationRef = useRef<string | null>(null)
  const previewFrameRef = useRef<HTMLDivElement | null>(null)
  const previewTextRef = useRef<HTMLDivElement | null>(null)
  const previewMeasurementRef = useRef<HTMLDivElement | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const soundAudioRefs = useRef(new Map<string, HTMLAudioElement>())
  const shortsCacheRef = useRef(
    new Map<
      string,
      {
        clips?: Array<{ id: string; title: string; partNumber: number; startSeconds: number; removed: boolean }>
        promise?: Promise<Array<{ id: string; title: string; partNumber: number; startSeconds: number; removed: boolean }>>
      }
    >()
  )
  const [previewVideo, setPreviewVideo] = useState<HTMLVideoElement | null>(
    null
  )
  const attachPreviewVideo = useCallback((player: HTMLVideoElement | null) => {
    videoRef.current = player
    setPreviewVideo(player)
  }, [])

  const applyFreshEditorData = useCallback((result: EditorData) => {
    setSelectedFormat(result.clip.format)
    setSelectedExportResolution(result.clip.exportResolution)
    setSubtitlesEnabled(result.clip.subtitlesEnabled)
    setSelectedSavedPresetId(null)
    setSelectedPreset(result.clip.subtitlePreset)
    setAppearance({
      textCase: result.clip.textCase,
      fontFamily: result.clip.fontFamily,
      textColor: result.clip.textColor,
      accentColor: result.clip.accentColor,
      bold: result.clip.bold,
      italic: result.clip.italic,
      maxWords: result.clip.maxWords,
    })
    setSelectedFontSize(result.clip.fontSizePx)
    setSelectedMarginHorizontal(result.clip.marginHorizontalPx)
    setSelectedPosition(result.clip.position)
    setFacecamOverride(result.clip.facecamOverride)
    setSceneFramingOverrides(result.clip.sceneFramingOverrides)
    setSceneColorFilters(result.clip.sceneColorFilters)
    setCaptionPositionSegments(
      defaultCaptionPositionSegments(
        result.clip.captionPositionSegments,
        rangeDuration(result.clip.cutRanges)
      )
    )
    setSelectedCaptionPositionSegment(0)
    setCutRanges(
      result.clip.cutRanges.length > 0
        ? result.clip.cutRanges
        : [
            {
              startSeconds: result.clip.startSeconds,
              endSeconds: result.clip.endSeconds,
            },
          ]
    )
    setUndoHistory([])
    setSelectedRange(null)
    setSounds(normalizeSounds(result.sounds))
    setImages(normalizeImages(result.images))
    setCaptionOverrides(result.captionOverrides)
    setCaptionTimingOverrides(result.clip.captionTimingOverrides)
    setSelectedCaptionIdentity(null)
    setSelectedSoundId(null)
    setSelectedImageId(null)
    setError(null)
  }, [])
  const { editor, sourceMedia, loading, loadError, load } = useClipEditorLoader(
    {
      videoId,
      clipId,
      dirtyRef,
      onFreshData: applyFreshEditorData,
    }
  )
  useEffect(() => {
    if (!shortsMenuOpen) return
    let cancelled = false
    const cached = shortsCacheRef.current.get(videoId)
    if (cached?.clips) {
      setShorts(cached.clips)
      setShortsError(false)
      setShortsLoading(false)
      return () => {
        cancelled = true
      }
    }
    setShortsLoading(true)
    setShortsError(false)
    const request = cached?.promise ?? videoClient.videos
      .get({ videoId })
      .then((result) => {
        const clips = result.clips
        shortsCacheRef.current.set(videoId, { clips })
        return clips
      })
    if (!cached?.promise) {
      shortsCacheRef.current.set(videoId, { promise: request })
    }
    void request
      .then((clips) => {
        if (!cancelled) setShorts(clips)
      })
      .catch(() => {
        shortsCacheRef.current.delete(videoId)
        if (!cancelled) setShortsError(true)
      })
      .finally(() => {
        if (!cancelled) setShortsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [shortsMenuOpen, videoId])
  const activeShorts = shorts.filter((short) => !short.removed)
  const selectedShortIndex = activeShorts.findIndex((short) => short.id === clipId)
  const draftIsDirty = useCallback(
    (overrides: Partial<EditorDraft> = {}) =>
      editor
        ? editorDirtyState(editor, {
            format: selectedFormat,
            exportResolution: selectedExportResolution,
            subtitlesEnabled,
            preset: selectedPreset,
            ...appearance,
            fontSizePx: selectedFontSize,
            marginHorizontalPx: selectedMarginHorizontal,
            position: selectedPosition,
            captionPositionSegments,
            cutRanges,
            sounds,
            images,
            captionOverrides,
            captionTimingOverrides,
            facecamOverride,
            sceneFramingOverrides,
            sceneColorFilters,
            ...overrides,
          }).any
        : false,
    [
      captionOverrides,
      captionTimingOverrides,
      captionPositionSegments,
      cutRanges,
      editor,
      facecamOverride,
      sceneFramingOverrides,
      sceneColorFilters,
      images,
      appearance,
      selectedFontSize,
      selectedMarginHorizontal,
      selectedPosition,
      selectedPreset,
      selectedFormat,
      selectedExportResolution,
      sounds,
      subtitlesEnabled,
    ]
  )
  const { updateCaption, resetCaptionEdits } = useCaptionEdits({
    editor,
    setCaptionOverrides,
    dirtyRef,
    draftVersionRef,
    draftIsDirty,
  })
  const clipDuration = rangeDuration(cutRanges)
  const activeImages = images.filter((image) => image.status !== "available")
  const activeSounds = sounds.filter((sound) => sound.status !== "available")
  const sourceTime = sourceTimeAtTimelineTime(cutRanges, currentTime, editor?.clip.startSeconds ?? 0)
  const sourceIsVisible = cutRanges.some(
    (range) => sourceTime >= range.startSeconds && (sourceTime < range.endSeconds || range === cutRanges.at(-1) && sourceTime === range.endSeconds)
  )
  const {
    uploadingSounds,
    addSoundFiles,
    addSoundAsset,
    duplicateSound,
    removeSound,
    selectSound,
    updateSound,
  } = useClipAudioMedia({
    videoId,
    clipId,
    editor,
    sounds,
    images,
    currentTime,
    clipDuration,
    draftVersionRef,
    dirtyRef,
    setSounds,
    setSelectedSoundId,
    setSelectedImageId,
    reload: load,
  })
  const {
    uploadingImages,
    addImageFiles,
    addImageAsset,
    duplicateImage,
    detachImage,
    selectImage,
    updateImage,
  } = useClipImageMedia({
    videoId,
    clipId,
    editor,
    images,
    sounds: activeSounds,
    currentTime,
    clipDuration,
    draftVersionRef,
    dirtyRef,
    setImages,
    setSelectedImageId,
    setSelectedSoundId,
    reload: load,
  })
  const { startImageTransform } = usePreviewImageInteractions({
    setSelectedImageId,
    setSelectedSoundId,
    setSelectedRange,
    updateImage,
    format: selectedFormat,
  })
  const selectedImage = activeImages.find(
    (image) => image.id === selectedImageId
  )
  const selectedSound = activeSounds.find(
    (sound) => sound.id === selectedSoundId
  )

  function compactMediaTracks(
    nextSounds: EditorSound[],
    nextImages: EditorImage[]
  ) {
    const used = [
      ...new Set([
        ...nextSounds
          .filter((sound) => sound.status !== "available")
          .map((sound) => sound.trackIndex),
        ...nextImages
          .filter((image) => image.status !== "available")
          .map((image) => image.trackIndex),
      ]),
    ].sort((a, b) => a - b)
    return {
      sounds: nextSounds.map((sound) =>
        sound.status === "available"
          ? sound
          : { ...sound, trackIndex: used.indexOf(sound.trackIndex) }
      ),
      images: nextImages.map((image) =>
        image.status === "available"
          ? image
          : { ...image, trackIndex: used.indexOf(image.trackIndex) }
      ),
    }
  }

  function commitMediaTracks(
    nextSounds: EditorSound[],
    nextImages: EditorImage[]
  ) {
    const compacted = compactMediaTracks(nextSounds, nextImages)
    soundsRef.current = compacted.sounds
    imagesRef.current = compacted.images
    setSounds(compacted.sounds)
    setImages(compacted.images)
  }

  function moveSoundToTrack(
    soundId: string,
    timelineStartSeconds: number,
    requestedTrackIndex: number
  ) {
    setSelectedRange(null)
    const currentSounds = soundsRef.current
    const currentImages = imagesRef.current
    const moving = currentSounds.find(
      (sound) => sound.id === soundId && sound.status !== "available"
    )
    if (!moving) return
    const end =
      timelineStartSeconds + moving.sourceEndSeconds - moving.sourceStartSeconds
    const overlaps = (trackIndex: number) =>
      currentSounds.some(
        (sound) =>
          sound.id !== soundId &&
          sound.status !== "available" &&
          sound.trackIndex === trackIndex &&
          timelineStartSeconds <
            sound.timelineStartSeconds +
              sound.sourceEndSeconds -
              sound.sourceStartSeconds &&
          end > sound.timelineStartSeconds
      ) ||
      currentImages.some(
        (image) =>
          image.status !== "available" &&
          image.trackIndex === trackIndex &&
          timelineStartSeconds < image.timelineEndSeconds &&
          end > image.timelineStartSeconds
      )
    let trackIndex = requestedTrackIndex
    while (overlaps(trackIndex)) trackIndex += 1
    commitMediaTracks(
      currentSounds.map((sound) =>
        sound.id === soundId
          ? { ...sound, timelineStartSeconds, trackIndex }
          : sound
      ),
      currentImages
    )
    draftVersionRef.current += 1
  }

  function moveImageToTrack(
    imageId: string,
    start: number,
    end: number,
    requestedTrackIndex?: number
  ) {
    setSelectedRange(null)
    const currentSounds = soundsRef.current
    const currentImages = imagesRef.current
    const moving = currentImages.find((image) => image.id === imageId)
    if (!moving) return
    let trackIndex = requestedTrackIndex ?? moving.trackIndex
    const overlaps = (candidateTrack: number) =>
      currentImages.some(
        (image) =>
          image.id !== imageId &&
          image.status !== "available" &&
          image.trackIndex === candidateTrack &&
          start < image.timelineEndSeconds &&
          end > image.timelineStartSeconds
      ) ||
      currentSounds.some(
        (sound) =>
          sound.status !== "available" &&
          sound.trackIndex === candidateTrack &&
          start <
            sound.timelineStartSeconds +
              sound.sourceEndSeconds -
              sound.sourceStartSeconds &&
          end > sound.timelineStartSeconds
      )
    while (overlaps(trackIndex)) trackIndex += 1
    commitMediaTracks(
      currentSounds,
      currentImages.map((image) =>
        image.id === imageId
          ? {
              ...image,
              timelineStartSeconds: start,
              timelineEndSeconds: end,
              trackIndex,
            }
          : image
      )
    )
    dirtyRef.current = true
    draftVersionRef.current += 1
  }

  function trimSoundToTrack(
    soundId: string,
    edge: "start" | "end",
    sourceTimeSeconds: number
  ) {
    setSelectedRange(null)
    const currentSounds = soundsRef.current
    const currentImages = imagesRef.current
    const moving = currentSounds.find(
      (sound) => sound.id === soundId && sound.status !== "available"
    )
    if (!moving) return
    const sourceStartSeconds =
      edge === "start"
        ? Math.max(
            0,
            Math.min(moving.sourceEndSeconds - 0.1, sourceTimeSeconds)
          )
        : moving.sourceStartSeconds
    const sourceEndSeconds =
      edge === "end"
        ? Math.min(
            moving.durationSeconds,
            Math.max(moving.sourceStartSeconds + 0.1, sourceTimeSeconds)
          )
        : moving.sourceEndSeconds
    const maxFade = (sourceEndSeconds - sourceStartSeconds) / 2
    const end =
      moving.timelineStartSeconds + sourceEndSeconds - sourceStartSeconds
    const overlaps = (candidateTrack: number) =>
      currentSounds.some(
        (sound) =>
          sound.id !== soundId &&
          sound.status !== "available" &&
          sound.trackIndex === candidateTrack &&
          moving.timelineStartSeconds <
            sound.timelineStartSeconds +
              sound.sourceEndSeconds -
              sound.sourceStartSeconds &&
          end > sound.timelineStartSeconds
      ) ||
      currentImages.some(
        (image) =>
          image.status !== "available" &&
          image.trackIndex === candidateTrack &&
          moving.timelineStartSeconds < image.timelineEndSeconds &&
          end > image.timelineStartSeconds
      )
    let trackIndex = moving.trackIndex
    while (overlaps(trackIndex)) trackIndex += 1
    commitMediaTracks(
      currentSounds.map((sound) =>
        sound.id === soundId
          ? {
              ...sound,
              sourceStartSeconds,
              sourceEndSeconds,
              fadeInSeconds: Math.min(sound.fadeInSeconds, maxFade),
              fadeOutSeconds: Math.min(sound.fadeOutSeconds, maxFade),
              trackIndex,
            }
          : sound
      ),
      currentImages
    )
    draftVersionRef.current += 1
  }

  function splitSoundOnTrack(soundId: string, splitTime: number) {
    setSelectedRange(null)
    if (!editor) return
    const currentSounds = soundsRef.current
    const currentImages = imagesRef.current
    const sound = currentSounds.find(
      (candidate) => candidate.id === soundId && candidate.status !== "available"
    )
    if (!sound) return
    const timelineEndSeconds =
      sound.timelineStartSeconds +
      sound.sourceEndSeconds -
      sound.sourceStartSeconds
    if (
      splitTime - sound.timelineStartSeconds < 0.1 ||
      timelineEndSeconds - splitTime < 0.1
    )
      return
    if (
      currentSounds.filter((candidate) => candidate.status !== "available")
        .length >= editor.soundLimits.maxSounds
    ) {
      toast.error(
        `A short can contain at most ${editor.soundLimits.maxSounds} sound tracks.`
      )
      return
    }
    const sourceSplit =
      sound.sourceStartSeconds + splitTime - sound.timelineStartSeconds
    const leftMaxFade = (sourceSplit - sound.sourceStartSeconds) / 2
    const rightMaxFade = (sound.sourceEndSeconds - sourceSplit) / 2
    const rightId = `local-sound-${crypto.randomUUID()}`
    const left: EditorSound = {
      ...sound,
      sourceEndSeconds: sourceSplit,
      fadeInSeconds: Math.min(sound.fadeInSeconds, leftMaxFade),
      fadeOutSeconds: Math.min(sound.fadeOutSeconds, leftMaxFade),
    }
    const right: EditorSound = {
      ...sound,
      id: rightId,
      sourceMediaId: sound.sourceMediaId ?? sound.id,
      timelineStartSeconds: splitTime,
      sourceStartSeconds: sourceSplit,
      fadeInSeconds: Math.min(sound.fadeInSeconds, rightMaxFade),
      fadeOutSeconds: Math.min(sound.fadeOutSeconds, rightMaxFade),
    }
    commitMediaTracks(
      currentSounds.flatMap((candidate) =>
        candidate.id === soundId ? [left, right] : [candidate]
      ),
      currentImages
    )
    setSelectedSoundId(rightId)
    setSelectedImageId(null)
    dirtyRef.current = true
    draftVersionRef.current += 1
  }

  function splitImageOnTrack(imageId: string, splitTime: number) {
    setSelectedRange(null)
    if (!editor) return
    const currentSounds = soundsRef.current
    const currentImages = imagesRef.current
    const image = currentImages.find(
      (candidate) => candidate.id === imageId && candidate.status !== "available"
    )
    if (!image) return
    if (
      splitTime - image.timelineStartSeconds < 0.1 ||
      image.timelineEndSeconds - splitTime < 0.1
    )
      return
    if (
      currentImages.filter((candidate) => candidate.status !== "available")
        .length >= editor.imageLimits.maxImages
    ) {
      toast.error(
        `A short can contain at most ${editor.imageLimits.maxImages} images.`
      )
      return
    }
    const rightId = `local-image-${crypto.randomUUID()}`
    const left: EditorImage = {
      ...image,
      timelineEndSeconds: splitTime,
    }
    const right: EditorImage = {
      ...image,
      id: rightId,
      sourceMediaId: image.sourceMediaId ?? image.id,
      timelineStartSeconds: splitTime,
    }
    commitMediaTracks(
      currentSounds,
      currentImages.flatMap((candidate) =>
        candidate.id === imageId ? [left, right] : [candidate]
      )
    )
    setSelectedImageId(rightId)
    setSelectedSoundId(null)
    dirtyRef.current = true
    draftVersionRef.current += 1
  }

  function addMediaFiles(files: File[]) {
    const soundFiles: File[] = []
    const imageFiles: File[] = []
    const unsupportedFiles: File[] = []

    for (const file of files) {
      const extension = file.name.split(".").at(-1)?.toLowerCase()
      if (["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
        imageFiles.push(file)
      } else if (extension === "mp3" || extension === "wav") {
        soundFiles.push(file)
      } else {
        unsupportedFiles.push(file)
      }
    }

    if (unsupportedFiles.length > 0) {
      toast.error(
        `${unsupportedFiles.map((file) => file.name).join(", ")} must be a JPEG, PNG, WebP, MP3, or WAV file.`
      )
    }
    if (imageFiles.length > 0) void addImageFiles(imageFiles)
    if (soundFiles.length > 0) void addSoundFiles(soundFiles)
  }
  const currentDefinition = editor?.presets.find(
    (preset) => preset.id === selectedPreset
  )
  const previewResolution = clipResolution(selectedFormat)
  const currentFramingPlan = editor?.clip.framingPlan
  const gameplaySegment = currentFramingPlan?.version === 2
    ? currentFramingPlan.segments.find(
        (segment) =>
          segment.layout === "gameplay" && segment.top && segment.bottom &&
          sourceTime >= segment.startSeconds && sourceTime < segment.endSeconds
      )
    : undefined
  const automaticFacecamRect = gameplaySegment?.top ?? null
  const effectiveFacecamRect = facecamOverride ?? automaticFacecamRect
  const canEditFacecam =
    selectedFormat === "9:16" && automaticFacecamRect !== null
  const facecamZoom =
    automaticFacecamRect && effectiveFacecamRect
      ? Math.round((automaticFacecamRect.width / effectiveFacecamRect.width) * 100)
      : 100
  const minimumFacecamZoom = automaticFacecamRect
    ? Math.ceil(Math.max(automaticFacecamRect.width, automaticFacecamRect.height) * 100)
    : 100
  const updateFacecamOverride = useCallback(
    (next: NonNullable<FacecamOverride> | null) => {
      draftVersionRef.current += 1
      dirtyRef.current = draftIsDirty({ facecamOverride: next })
      setFacecamOverride(next)
    },
    [dirtyRef, draftIsDirty]
  )

  const setFacecamZoom = useCallback(
    (zoomPercent: number) => {
      if (!automaticFacecamRect || !effectiveFacecamRect) return
      const zoom = Math.max(minimumFacecamZoom, Math.min(300, zoomPercent)) / 100
      const width = Math.min(1, automaticFacecamRect.width / zoom)
      const height = Math.min(1, automaticFacecamRect.height / zoom)
      const centerX = effectiveFacecamRect.x + effectiveFacecamRect.width / 2
      const centerY = effectiveFacecamRect.y + effectiveFacecamRect.height / 2
      updateFacecamOverride({
        x: Math.max(0, Math.min(1, centerX)) - width / 2,
        y: Math.max(0, Math.min(1, centerY)) - height / 2,
        width,
        height,
      })
    },
    [automaticFacecamRect, effectiveFacecamRect, minimumFacecamZoom, updateFacecamOverride]
  )

  const startFacecamDrag = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!canEditFacecam || !effectiveFacecamRect) return
      setVideoPaused(true)
      event.currentTarget.setPointerCapture(event.pointerId)
      facecamDragRef.current = {
        pointerId: event.pointerId,
        clientX: event.clientX,
        clientY: event.clientY,
        rect: effectiveFacecamRect,
      }
    },
    [canEditFacecam, effectiveFacecamRect]
  )

  const moveFacecamDrag = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const drag = facecamDragRef.current
      const bounds = event.currentTarget.getBoundingClientRect()
      if (!drag || drag.pointerId !== event.pointerId || bounds.width <= 0 || bounds.height <= 0) return
      const x = Math.max(
        -drag.rect.width / 2,
        Math.min(1 - drag.rect.width / 2, drag.rect.x - (event.clientX - drag.clientX) / bounds.width * drag.rect.width)
      )
      const y = Math.max(
        -drag.rect.height / 2,
        Math.min(1 - drag.rect.height / 2, drag.rect.y - (event.clientY - drag.clientY) / bounds.height * drag.rect.height)
      )
      updateFacecamOverride({ ...drag.rect, x, y })
    },
    [updateFacecamOverride]
  )

  const stopFacecamDrag = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId)
    facecamDragRef.current = null
  }, [])
  const currentDefaultPosition: SubtitlePosition = {
    x: 50,
    y: automaticCaptionY(currentFramingPlan, sourceTime, selectedFormat, sceneFramingOverrides) ?? 100 -
        ((currentDefinition?.preview.marginBottom ?? 230) /
          previewResolution.height) *
          100,
  }
  const activeCaptionPositionSegment = Math.max(
    0,
    captionPositionSegmentAt(captionPositionSegments, Math.min(clipDuration, currentTime + SUBTITLE_PRESENTATION_LEAD_SECONDS))
  )
  const effectivePosition =
    captionPositionSegments[activeCaptionPositionSegment]?.position ??
    selectedPosition ??
    currentDefaultPosition
  const updateActiveCaptionPosition = useCallback(
    (position: SubtitlePosition) => {
      const cue = positionedCaptionRef.current
      if (!cue) return
      let next: CaptionPositionSegment[]
      try {
        next = positionCaptionRange(captionPositionSegments, cue.start, cue.end, position)
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "Could not update this subtitle position.")
        return
      }
      draftVersionRef.current += 1
      dirtyRef.current = draftIsDirty({ captionPositionSegments: next })
      setCaptionPositionSegments(next)
      setSelectedCaptionPositionSegment(Math.max(0, captionPositionSegmentAt(next, currentTime)))
    },
    [captionPositionSegments, currentTime, dirtyRef, draftIsDirty]
  )
  const {
    clampPosition,
    dragging,
    movePositionFromPointer,
    movePositionWithKeyboard,
    startDragging,
    stopDragging,
  } = useCaptionPositionInteractions({
    enabled: subtitlesEnabled,
    editorReady: Boolean(editor),
    effectivePosition,
    frameRef: previewFrameRef,
    textRef: previewTextRef,
    measurementRef: previewMeasurementRef,
    videoRef,
    dirtyRef,
    draftVersionRef,
    draftIsDirty,
    setPosition: setSelectedPosition,
    updatePosition: updateActiveCaptionPosition,
    setVideoPaused,
  })
  const { saving, saveStyle } = useClipEditorSave({
    videoId,
    clipId,
    editor,
    draft: {
      format: selectedFormat,
      exportResolution: selectedExportResolution,
      subtitlesEnabled,
      preset: selectedPreset,
      ...appearance,
      fontSizePx: selectedFontSize,
      marginHorizontalPx: selectedMarginHorizontal,
      position: selectedPosition,
      captionPositionSegments,
      cutRanges,
      sounds,
      images,
      captionOverrides,
      captionTimingOverrides,
      facecamOverride,
      sceneFramingOverrides,
      sceneColorFilters,
    },
    routeRef: editorRouteRef,
    dirtyRef,
    draftVersionRef,
    clampPosition,
    reload: load,
    onError: setError,
    onSaved: () => {
      toast.success("Changes saved. Render queued.")
      if (switchTarget) {
        const target = switchTarget
        setSwitchTarget(null)
        navigate({ to: "/app/videos/$videoId/clips/$clipId/edit", params: { videoId, clipId: target } })
      }
    },
  })
  const videoSync = useVideoPreviewSync({
    videoRef,
    snapshotCanvasRef,
    cutRanges,
    currentTime,
    duration: clipDuration,
    paused: videoPaused,
    initialSourceTime: editor?.clip.startSeconds ?? 0,
    setCurrentTime,
    setPaused: setVideoPaused,
    framingPlan:
      selectedFormat === "9:16" ? (editor?.clip.framingPlan ?? null) : null,
    facecamOverride: selectedFormat === "9:16" ? facecamOverride : null,
    format: selectedFormat,
    setSnapshotSourceTime: () => undefined,
    onError: setError,
  })
  const { updateCutRanges, undo, removeSelectedRange } = useEditorCutActions(
    {
      sourceScenes: editor?.clip.sourceScenes ?? [],
      sceneFramingOverrides,
      sceneColorFilters,
      setSceneFramingOverrides,
      setSceneColorFilters,
      onError: setError,
      maxRanges: editor?.editLimits.maxRanges ?? null,
      cutRanges,
      undoHistory,
      selectedRange,
      sounds,
      images,
      captionPositionSegments,
      captionTimingOverrides,
      setCaptionTimingOverrides,
      setCutRanges,
      setUndoHistory,
      setSelectedRange,
      setSelectedSoundId,
      setSelectedImageId,
      setSounds,
      setImages,
      setCaptionPositionSegments,
      setSelectedCaptionPositionSegment,
      dirtyRef,
      draftVersionRef,
      draftIsDirty,
      syncPreviewToRanges: videoSync.syncToRanges,
    }
  )

  useEffect(() => {
    if (!editor || editor.clip.id !== clipId || editor.clip.sceneStatus !== "ready" || editor.clip.sourceScenes.length === 0) return
    const segmentationKey = JSON.stringify([
      `${videoId}/${clipId}`,
      editor.clip.sceneIdentity,
      editor.clip.sourceScenes.map(scene => [scene.startSeconds, scene.endSeconds, scene.kind]),
    ])
    if (appliedSceneSegmentationRef.current === segmentationKey) return
    appliedSceneSegmentationRef.current = segmentationKey

    const next = applySceneBoundaries(
      cutRanges,
      editor.clip.sourceScenes,
      editor.editLimits.minRangeSeconds
    )
    if (sameRanges(next, cutRanges)) return
    if (next.length > editor.editLimits.maxRanges) {
      setSceneRequestError(`Applying these scenes would exceed the limit of ${editor.editLimits.maxRanges} retained ranges.`)
      return
    }
    setSceneRequestError(null)
    updateCutRanges(next)
  }, [clipId, cutRanges, editor, updateCutRanges, videoId])

  function selectVideoRange(index: number | null) {
    setSelectedSoundId(null)
    setSelectedImageId(null)
    setSelectedRange(index)
  }

  function selectTimelineSound(soundId: string) {
    setSelectedRange(null)
    selectSound(soundId)
  }

  function selectTimelineImage(imageId: string) {
    setSelectedRange(null)
    selectImage(imageId)
  }

  function removeTimelineSound(sound: EditorSound) {
    setUndoHistory((history) => [
      ...history,
      { kind: "sound-detach", sound: { ...sound } },
    ])
    setSelectedRange(null)
    removeSound(sound)
  }

  function detachTimelineImage(image: EditorImage) {
    setUndoHistory((history) => [
      ...history,
      { kind: "image-detach", image: { ...image } },
    ])
    setSelectedRange(null)
    detachImage(image.id)
  }

  useEffect(() => {
    if (selectedSoundId !== null || selectedImageId !== null) {
      setSelectedRange(null)
    }
  }, [selectedImageId, selectedSoundId])

  useEffect(() => {
    editorRouteRef.current = `${videoId}/${clipId}`
    dirtyRef.current = false
    draftVersionRef.current = 0
    setSubtitlesEnabled(true)
    setSelectedExportResolution(1080)
    setSelectedPreset(null)
    setSelectedFontSize(null)
    setSelectedMarginHorizontal(null)
    setSelectedPosition(null)
    setFacecamOverride(null)
    setSceneFramingOverrides(null)
    setCaptionPositionSegments([])
    setSelectedCaptionPositionSegment(0)
    setCutRanges([])
    setUndoHistory([])
    setSelectedRange(null)
    setSounds([])
    setImages([])
    setCaptionOverrides([])
    setSelectedSoundId(null)
    setSelectedImageId(null)
    setVideoVolume(100)
    setError(null)
  }, [clipId, dirtyRef, videoId])

  useEffect(() => {
    const changeSection = (event: Event) => {
      const section = (event as CustomEvent<string>).detail
      if (!["video", "captions", "reframing"].includes(section)) return
      setActiveWorkspace(section)
    }
    window.addEventListener("clip-editor-section", changeSection)
    return () =>
      window.removeEventListener("clip-editor-section", changeSection)
  }, [])

  const renderIsActive =
    editor?.clip.renderStatus === "pending" ||
    editor?.clip.renderStatus === "rendering"

  const renderSaveBlocked = editor?.clip.renderSaveBlocked ?? false

  useEffect(() => {
    const toastId = `subtitle-render-${videoId}-${clipId}`
    if (!renderIsActive) {
      toast.dismiss(toastId)
      return
    }
    toast.loading(
      editor.clip.renderQueued ? "Render queued" : "Rendering is in progress",
      {
        id: toastId,
        description: renderSaveBlocked
          ? "You can edit and save another short while this export renders."
          : "Your changes are saved. You can keep editing this short or open another one.",
        descriptionClassName: "app-editor-render-toast-description",
        duration: Infinity,
        dismissible: false,
        closeButton: false,
      }
    )
    return () => {
      toast.dismiss(toastId)
    }
  }, [clipId, editor?.clip.renderQueued, renderIsActive, renderSaveBlocked, videoId])

  useEffect(() => {
    function warnBeforeUnload(event: BeforeUnloadEvent) {
      if (!dirtyRef.current) return
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", warnBeforeUnload)
    return () => window.removeEventListener("beforeunload", warnBeforeUnload)
  }, [])

  useSoundPlaybackSync({
    audioRefs: soundAudioRefs,
    currentTime,
    muted: videoMuted,
    paused: videoPaused,
    sounds: activeSounds,
  })

  useEffect(() => {
    if (videoRef.current) videoRef.current.volume = videoVolume / 100
  }, [videoVolume])

  useEffect(() => {
    if (!editor || !selectedPosition || !subtitlesEnabled) return
    const animationFrame = window.requestAnimationFrame(() => {
      const clampedPosition = clampPosition(selectedPosition)
      if (samePosition(clampedPosition, selectedPosition)) return
      dirtyRef.current = draftIsDirty({ position: clampedPosition })
      draftVersionRef.current += 1
      setSelectedPosition(clampedPosition)
    })
    return () => window.cancelAnimationFrame(animationFrame)
  }, [
    cutRanges,
    captionOverrides,
    captionTimingOverrides,
    editor,
    appearance,
    selectedFontSize,
    selectedMarginHorizontal,
    selectedPosition,
    selectedPreset,
    sounds,
    subtitlesEnabled,
    draftIsDirty,
  ])

  useEffect(() => {
    if (
      loading ||
      !editor ||
      !selectedPreset ||
      selectedFontSize === null ||
      selectedMarginHorizontal === null
    )
      return

    function handleKeyDown(event: globalThis.KeyboardEvent) {
      const target = event.target as HTMLElement | null
      if (
        target?.closest(
          "input, textarea, select, [contenteditable], [role=dialog], [role=alertdialog], [data-slot=field]"
        )
      )
        return
      if (event.key === " " || event.key === "Spacebar") {
        event.preventDefault()
        videoSync.togglePlayback()
        return
      }
      if (
        activeWorkspace === "video" &&
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === "c"
      ) {
        const image = images.find(
          (candidate) =>
            candidate.id === selectedImageId &&
            candidate.status !== "available"
        )
        const sound = activeSounds.find(
          (candidate) => candidate.id === selectedSoundId
        )
        if (image) {
          event.preventDefault()
          setMediaClipboard({ kind: "image", snapshot: { ...image } })
        } else if (sound) {
          event.preventDefault()
          setMediaClipboard({ kind: "sound", snapshot: { ...sound } })
        }
        return
      }
      if (
        activeWorkspace === "video" &&
        mediaClipboard &&
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === "v"
      ) {
        const canPaste =
          mediaClipboard.kind === "sound"
            ? activeSounds.length < editor!.soundLimits.maxSounds
            : images.filter((image) => image.status !== "available").length <
              editor!.imageLimits.maxImages
        if (!canPaste) return
        event.preventDefault()
        if (mediaClipboard.kind === "image")
          void duplicateImage(mediaClipboard.snapshot, currentTime)
        else void duplicateSound(mediaClipboard.snapshot, currentTime)
        return
      }
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === "z" &&
        target?.closest("[data-clip-timeline]")
      ) {
        if (undoHistory.length === 0) return
        event.preventDefault()
        undo()
      }
      if (
        (event.key === "Backspace" || event.key === "Delete") &&
        selectedImageId !== null &&
        images.some(
          (image) =>
            image.id === selectedImageId && image.status !== "available"
        )
      ) {
        event.preventDefault()
        const image = images.find((candidate) => candidate.id === selectedImageId)
        if (image) detachTimelineImage(image)
        return
      }
      if (
        activeWorkspace === "video" &&
        (event.key === "Backspace" || event.key === "Delete")
      ) {
        const sound = activeSounds.find(
          (candidate) => candidate.id === selectedSoundId
        )
        if (sound) {
          event.preventDefault()
          removeTimelineSound(sound)
          return
        }
      }
      if (
        (event.key === "Backspace" || event.key === "Delete") &&
        selectedRange !== null &&
        target?.closest("[data-clip-timeline]")
      ) {
        event.preventDefault()
        removeSelectedRange()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [
    loading,
    editor,
    selectedPreset,
    subtitlesEnabled,
    appearance,
    selectedFontSize,
    selectedMarginHorizontal,
    undoHistory,
    cutRanges,
    selectedRange,
    selectedImageId,
    selectedSoundId,
    images,
    sounds,
    activeSounds,
    currentTime,
    activeWorkspace,
    mediaClipboard,
    duplicateImage,
    duplicateSound,
    videoSync.togglePlayback,
  ])

  useEffect(() => {
    if (!editor) return
    dirtyRef.current = draftIsDirty()
  }, [dirtyRef, draftIsDirty, editor])

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        <Spinner className="size-5 text-muted-foreground" />
      </div>
    )
  }

  if (
    !editor ||
    !selectedPreset ||
    selectedFontSize === null ||
    selectedMarginHorizontal === null
  ) {
    return (
      <main className="app-main app-editor-page">
        <Link
          to="/app/videos/$videoId"
          params={{ videoId }}
          className={buttonVariants({
            variant: "ghost",
            size: "sm",
            className: "self-start",
          })}
        >
          ← Back to video
        </Link>
        <Alert variant="destructive">
          <AlertCircle aria-hidden="true" />
          <AlertTitle>Unable to load the editor</AlertTitle>
          <AlertDescription>
            {error ?? loadError ?? "The editor is temporarily unavailable."}
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => void load()}>
          Try again
        </Button>
      </main>
    )
  }

  const selectedDefinition = editor.presets.find(
    (preset) => preset.id === selectedPreset
  )
  const subtitleAvailableWidthPx =
    previewResolution.width -
    (selectedMarginHorizontal + SUBTITLE_SAFE_EDGE_PADDING_PX) * 2
  const subtitleAvailableWidthPercent =
    (subtitleAvailableWidthPx / previewResolution.width) * 100
  const dirtyState = editorDirtyState(editor, {
    format: selectedFormat,
    exportResolution: selectedExportResolution,
    subtitlesEnabled,
    preset: selectedPreset,
    ...appearance,
    fontSizePx: selectedFontSize,
    marginHorizontalPx: selectedMarginHorizontal,
    position: selectedPosition,
    captionPositionSegments,
    cutRanges,
    sounds,
    images,
    captionOverrides,
    captionTimingOverrides,
    facecamOverride,
    sceneFramingOverrides,
    sceneColorFilters,
  })
  const {
    captions: captionsAreDirty,
    any: isDirty,
    retainedCaptionOverrides: retainedOverrides,
  } = dirtyState
  const needsFirstStyledRender = editor.clip.renderRevision === 0
  const canSaveDraft =
    !renderSaveBlocked &&
    !saving &&
    uploadingSounds === 0 &&
    uploadingImages === 0 &&
    retainedOverrides.every((override) => override.text.trim().length > 0)
  const canSave =
    canSaveDraft &&
    (isDirty || needsFirstStyledRender || editor.clip.renderStatus === "failed")
  const canDownload = !isDirty && !saving && !downloading &&
    uploadingSounds === 0 && uploadingImages === 0 &&
    editor.clip.renderStatus === "ready" &&
    editor.clip.renderedRevision === editor.clip.renderRevision
  const sceneCutRanges = applySceneBoundaries(
    cutRanges,
    editor.clip.sourceScenes,
    editor.editLimits.minRangeSeconds
  )
  const sceneBoundariesApplied = editor.clip.sceneStatus === "ready" && sameRanges(
    cutRanges,
    applySceneBoundaries(
      cutRanges,
      editor.clip.sourceScenes,
      editor.editLimits.minRangeSeconds
    )
  )
  const canApplyScenes = canSaveDraft && (
    !sameRanges(sceneCutRanges, editor.clip.cutRanges) ||
    isDirty || needsFirstStyledRender || editor.clip.renderStatus === "failed"
  )
  const sceneDetectionControl = (
    <>
      {(editor.clip.sceneStatus === "idle" || editor.clip.sceneStatus === "failed") && (
        <Button type="button" variant="accent" size="sm" disabled={sceneRequestPending || !canSaveDraft || editor.video.renderJobStatus === "queued" || editor.video.renderJobStatus === "running"} onClick={() => {
          setSceneRequestPending(true)
          setSceneRequestError(null)
          void videoClient.clips.detectScenes({ videoId, clipId }).then(() => load()).catch((cause: unknown) => {
            setSceneRequestError(cause instanceof Error ? cause.message : "Scene detection could not be started.")
          }).finally(() => setSceneRequestPending(false))
        }}>
          {sceneRequestPending
            ? <Spinner data-icon="inline-start" />
            : <Sparkles data-icon="inline-start" aria-hidden="true" />}
          {editor.clip.sceneStatus === "failed" ? "Retry scene detection" : "Detect scenes"}
        </Button>
      )}
      {(editor.clip.sceneStatus === "pending" || editor.clip.sceneStatus === "running") && <span role="status" className="flex items-center gap-2"><Spinner />Detecting visual and narrative scenes…</span>}
    </>
  )
  const baselineOverrides = retainedCaptionOverrides(
    mergedCaptionOverrides(
      editor.captionOverrides,
      editor.captionMemoryOverrides
    ),
    editor.subtitleWords,
    cutRanges
  )
  const baselineCues = cuesForRanges(
    editor.subtitleWords,
    cutRanges,
    baselineOverrides,
    editor.clip.startSeconds,
    editor.captionOverrides
  )
  const editorCues = captionsWithDrafts(
    baselineCues,
    retainedOverrides,
    editor.subtitleWords,
    cutRanges,
    editor.clip.startSeconds
  )
  const timedEditorCues = applyCaptionTimingOverrides(
    editorCues,
    captionTimingOverrides
  )
  const displayCues = fitPreviewCues(
    timedEditorCues,
    selectedFontSize,
    selectedMarginHorizontal,
    previewResolution.width,
    appearance.maxWords
  )
  const presentationTime = Math.min(
    clipDuration,
    currentTime + SUBTITLE_PRESENTATION_LEAD_SECONDS
  )
  const activeCue = sourceIsVisible
    ? displayCues.find(
        (cue) => presentationTime >= cue.start && presentationTime < cue.end
      )
    : undefined

  const activeWordIndex = activeCue ? activeCaptionWordIndex(activeCue, presentationTime) : -1
  const activeWord = activeCue?.words[activeWordIndex]
  const activeEditorCue = activeCue ? timedEditorCues.find((cue) =>
    cue.startWordIndex === activeCue.startWordIndex && cue.endWordIndex === activeCue.endWordIndex) : undefined
  positionedCaptionRef.current = activeEditorCue
  const activeTextRange = !videoPaused && subtitlesEnabled && activeWord && activeEditorCue
    ? captionWordTextRange(activeEditorCue, activeWord)
    : null
  const selectedEditorCue = videoPaused && captionFocusRequest
    ? editorCues.find((cue) => `${cue.startWordIndex}:${cue.endWordIndex}` === captionFocusRequest.cueKey)
    : undefined
  const firstSelectedWord = selectedEditorCue?.text.trim().split(/\s+/u)[0]
  const activeCaptionWord = firstSelectedWord && captionFocusRequest ? {
    cueKey: captionFocusRequest.cueKey,
    start: 0,
    end: firstSelectedWord.normalize("NFC").length,
  } : activeTextRange && activeEditorCue ? {
    cueKey: `${activeEditorCue.startWordIndex}:${activeEditorCue.endWordIndex}`,
    ...activeTextRange,
  } : null

  const updateAppearance = (updates: Partial<CaptionAppearance>) => {
    const next = { ...appearance, ...updates }
    draftVersionRef.current += 1
    dirtyRef.current = draftIsDirty(next)
    setAppearance(next)
  }
  const allPresetOptions = captionPresetOptions({
    ...editor,
    savedCaptionPresets: [
      ...editor.savedCaptionPresets,
      ...newSavedPresets.filter((saved) => !editor.savedCaptionPresets.some((existing) => existing.id === saved.id)),
    ],
  })
  const defaultPresetOptions = allPresetOptions.filter((option) => option.style === null)
  const savedPresetOptions = allPresetOptions.filter((option) => option.style !== null)
  const applyPreset = (option: CaptionPresetOption) => {
    draftVersionRef.current += 1
    setSelectedSavedPresetId(option.style ? option.selectionId : null)
    setSelectedPreset(option.id)
    if (option.style) {
      const { textCase, fontFamily, textColor, accentColor, bold, italic, maxWords, fontSizePx, marginHorizontalPx, position } = option.style
      const nextAppearance = { textCase, fontFamily, textColor, accentColor, bold, italic, maxWords: maxWords ?? SUBTITLE_DEFAULT_MAX_WORDS }
      setAppearance(nextAppearance)
      setSelectedFontSize(fontSizePx)
      setSelectedMarginHorizontal(marginHorizontalPx)
      setSelectedPosition(position)
      setCaptionPositionSegments([{ startSeconds: 0, endSeconds: Math.round(clipDuration * 1000) / 1000, position }])
      setSelectedCaptionPositionSegment(0)
    }
  }
  const defaultAccentColor = selectedPreset === "yellow_outline" ? "#FFFF00"
    : selectedPreset === "mosy" ? "#8FFFCB"
    : selectedPreset === "think_media" ? "#34D7FF"
    : selectedPreset === "neon" ? "#FFFFFF"
    : ["active_word_highlight", "hormozi", "karaoke"].includes(selectedPreset) ? "#FFD90F"
    : ["bold_yellow", "beasty"].includes(selectedPreset) ? "#FFD400"
    : selectedDefinition?.preview.color ?? "#FFFFFF"
  const previewStyle = selectedDefinition?.preview
  const showPlacementPreview = Boolean(activeCue) || videoPaused

  const previewPosition = clampPosition(effectivePosition)
  const showVerticalGuide = previewPosition.x === 50
  const showHorizontalGuide = previewPosition.y === 50

  const captionTextTransform = appearance.textCase ??
    (["beasty", "podpi", "think_media", "yellow_outline"].includes(selectedPreset) ? "uppercase" : "none")
  const subtitleVisualStyle = previewStyle
    ? {
        textTransform: captionTextTransform,
        fontFamily: appearance.fontFamily ? captionFontFamilies[appearance.fontFamily] : previewStyle.fontFamily,
        fontWeight: fixedFontWeight ?? (appearance.bold === null ? previewStyle.fontWeight : appearance.bold ? 700 : 400),
        fontStyle: fixedFontWeight === undefined && appearance.italic ? "italic" : "normal",
        fontSynthesis: fixedFontWeight === undefined ? undefined : "none",
        fontSize: `${selectedFontSize / (previewResolution.width / 100)}cqw`,
        color: appearance.textColor ?? previewStyle.color,
        backgroundColor: previewStyle.backgroundColor,
        WebkitTextStroke: `${previewStyle.strokeWidth / (previewResolution.width / 100)}cqw ${previewStyle.strokeColor}`,
        textShadow:
          previewStyle.shadowWidth > 0
            ? `0 ${previewStyle.shadowWidth / (previewResolution.width / 100)}cqw ${previewStyle.shadowWidth / (previewResolution.width / 100)}cqw ${previewStyle.strokeColor}`
            : undefined,
        left: `${previewPosition.x}%`,
        top: `${previewPosition.y}%`,
        right: "auto",
        bottom: "auto",
        transform: "translate(-50%, -100%)",
        width: "fit-content",
        maxWidth: `${subtitleAvailableWidthPercent}%`,
      }
    : undefined
  const subtitleVisualStyleForCue = (cue: PreviewCaptionCue | undefined) =>
    subtitleVisualStyle
      ? {
          ...subtitleVisualStyle,
          fontSize: `${(cue?.fontSizePx ?? selectedFontSize) / (previewResolution.width / 100)}cqw`,
        }
      : undefined
  const updateSelectedImageSize = (percent: number) => {
    if (!selectedImage) return
    const sourceRatio =
      selectedImage.sourceWidth && selectedImage.sourceHeight
        ? selectedImage.sourceWidth / selectedImage.sourceHeight
        : 1
    const normalizedRatio =
      sourceRatio / (previewResolution.width / previewResolution.height)
    const width = Math.max(
      0.01,
      Math.min(
        percent / 100,
        1 - selectedImage.x,
        (1 - selectedImage.y) * normalizedRatio
      )
    )
    updateImage(selectedImage.id, { width, height: width / normalizedRatio })
  }
  const mediaInspector = selectedImage ? (
    <section className="app-editor-media-inspector" aria-labelledby="image-inspector-title">
      <div className="app-editor-media-inspector-heading">
        <img src={selectedImage.media.url} alt="" />
        <div><h3 id="image-inspector-title">Image</h3><p title={selectedImage.filename}>{selectedImage.filename}</p></div>
      </div>
      <FieldGroup>
        <Field><FieldLabel htmlFor="image-size">Size · {Math.round(selectedImage.width * 100)}%</FieldLabel><Slider id="image-size" value={[selectedImage.width * 100]} min={1} max={100} step={1} aria-label={`Size for ${selectedImage.filename}`} onValueChange={(value) => updateSelectedImageSize(typeof value === "number" ? value : (value[0] ?? 1))} /></Field>
        <Field><FieldLabel htmlFor="image-opacity">Opacity · {Math.round(selectedImage.opacity * 100)}%</FieldLabel><Slider id="image-opacity" value={[selectedImage.opacity * 100]} min={0} max={100} step={1} aria-label={`Opacity for ${selectedImage.filename}`} onValueChange={(value) => updateImage(selectedImage.id, { opacity: (typeof value === "number" ? value : (value[0] ?? 100)) / 100 })} /></Field>
        <Field><FieldLabel htmlFor="image-position-x">Position X · {Math.round(selectedImage.x * 100)}%</FieldLabel><Slider id="image-position-x" value={[selectedImage.x * 100]} min={0} max={(1 - selectedImage.width) * 100} step={1} aria-label={`Horizontal position for ${selectedImage.filename}`} onValueChange={(value) => updateImage(selectedImage.id, { x: (typeof value === "number" ? value : (value[0] ?? 0)) / 100 })} /></Field>
        <Field><FieldLabel htmlFor="image-position-y">Position Y · {Math.round(selectedImage.y * 100)}%</FieldLabel><Slider id="image-position-y" value={[selectedImage.y * 100]} min={0} max={(1 - selectedImage.height) * 100} step={1} aria-label={`Vertical position for ${selectedImage.filename}`} onValueChange={(value) => updateImage(selectedImage.id, { y: (typeof value === "number" ? value : (value[0] ?? 0)) / 100 })} /></Field>
      </FieldGroup>
    </section>
  ) : selectedSound ? (() => {
    const maxFade = Math.max(0, selectedSound.sourceEndSeconds - selectedSound.sourceStartSeconds) / 2
    return (
      <section className="app-editor-media-inspector" aria-labelledby="sound-inspector-title">
        <div className="app-editor-media-inspector-heading"><div><h3 id="sound-inspector-title">Sound</h3><p title={selectedSound.filename}>{selectedSound.filename}</p></div></div>
        <FieldGroup>
          <Field><FieldLabel htmlFor="sound-volume">Volume · {Math.round(selectedSound.volume * 100)}%</FieldLabel><Slider id="sound-volume" value={[selectedSound.volume * 100]} min={0} max={100} step={1} aria-label={`Volume for ${selectedSound.filename}`} onValueChange={(value) => updateSound(selectedSound.id, { volume: (typeof value === "number" ? value : (value[0] ?? 100)) / 100 })} /></Field>
          <Field><FieldLabel htmlFor="sound-fade-in">Fade in · {selectedSound.fadeInSeconds.toFixed(1)}s</FieldLabel><Slider id="sound-fade-in" value={[selectedSound.fadeInSeconds]} min={0} max={maxFade} step={0.1} aria-label={`Fade in for ${selectedSound.filename}`} onValueChange={(value) => updateSound(selectedSound.id, { fadeInSeconds: typeof value === "number" ? value : (value[0] ?? 0) })} /></Field>
          <Field><FieldLabel htmlFor="sound-fade-out">Fade out · {selectedSound.fadeOutSeconds.toFixed(1)}s</FieldLabel><Slider id="sound-fade-out" value={[selectedSound.fadeOutSeconds]} min={0} max={maxFade} step={0.1} aria-label={`Fade out for ${selectedSound.filename}`} onValueChange={(value) => updateSound(selectedSound.id, { fadeOutSeconds: typeof value === "number" ? value : (value[0] ?? 0) })} /></Field>
        </FieldGroup>
        <Button type="button" variant="destructive" size="sm" onClick={() => removeTimelineSound(selectedSound)}><Trash2 data-icon="inline-start" aria-hidden="true" />Remove sound</Button>
      </section>
    )
  })() : null
  const duplicatePresetName = Boolean(presetName.trim()) && [...editor.savedCaptionPresets.map((saved) => saved.name), ...newSavedPresets.map((saved) => saved.name), ...editor.presets.map((preset) => preset.label)].some((name) => normalizeCaptionPresetName(name) === normalizeCaptionPresetName(presetName))
  const presetNameError = duplicatePresetName ? "A preset with this name already exists." : presetError
  async function saveCaptionPreset() {
    if (!editor || !selectedPreset || selectedFontSize === null || selectedMarginHorizontal === null || presetSaving || !presetName.trim() || duplicatePresetName) return
    setPresetSaving(true)
    setPresetError(null)
    try {
      const saved = await videoClient.captionPresets.create({
        name: presetName.trim(),
        style: { preset: selectedPreset, ...appearance, fontSizePx: selectedFontSize, marginHorizontalPx: selectedMarginHorizontal, position: effectivePosition },
      })
      setNewSavedPresets((current) => [...current, saved])
      setSavePresetOpen(false)
      toast.success("Caption preset saved")
    } catch (saveError) {
      setPresetError(saveError instanceof Error ? saveError.message : "The caption preset could not be saved.")
    } finally { setPresetSaving(false) }
  }
  return (
    <main
      className={`app-main app-editor-page app-editor-workspace app-editor-workspace-${activeWorkspace}`}
    >
      <Dialog open={savePresetOpen} onOpenChange={(open) => { if (!presetSaving) setSavePresetOpen(open) }}>
        <DialogContent showCloseButton={!presetSaving}>
          <DialogHeader>
            <DialogTitle>Save caption preset</DialogTitle>
            <DialogDescription>Save this style and the current fixed position for other shorts. Timed position changes are excluded.</DialogDescription>
          </DialogHeader>
          <form onSubmit={(event) => { event.preventDefault(); void saveCaptionPreset() }} className="flex flex-col gap-6">
            <FieldGroup>
              <Field data-invalid={Boolean(presetNameError)}>
                <FieldLabel htmlFor="caption-preset-name">Preset name</FieldLabel>
                <Input id="caption-preset-name" value={presetName} maxLength={80} required autoFocus disabled={presetSaving} aria-invalid={Boolean(presetNameError)} aria-describedby={presetNameError ? "caption-preset-name-error" : undefined} onChange={(event) => { setPresetName(event.target.value); setPresetError(null) }} />
                {presetNameError && <FieldDescription id="caption-preset-name-error" role="alert">{presetNameError}</FieldDescription>}
              </Field>
            </FieldGroup>
            <DialogFooter>
              <Button type="button" variant="outline" disabled={presetSaving} onClick={() => setSavePresetOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={presetSaving || !presetName.trim() || duplicatePresetName}>{presetSaving && <Spinner data-icon="inline-start" />}Save preset</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <section className="app-editor-heading" aria-label="Editor actions">
        <Link
          to="/app/videos/$videoId"
          params={{ videoId }}
          className={buttonVariants({
            variant: "ghost",
            size: "sm",
            className: "self-start",
          })}
        >
          ← Back to video
        </Link>
        <DropdownMenu open={shortsMenuOpen} onOpenChange={setShortsMenuOpen}>
          <DropdownMenuTrigger
            render={
              <Button variant="outline" size="sm" className="max-w-72 justify-between gap-3" />
            }
          >
            <span className="truncate">{editor.clip.title}</span>
            <span className="text-muted-foreground">
              #{selectedShortIndex >= 0 ? selectedShortIndex + 1 : "—"}
            </span>
            <ChevronDown data-icon="inline-end" aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-96" align="start">
            <DropdownMenuGroup>
              {shortsLoading ? (
                <DropdownMenuItem disabled>Loading shorts…</DropdownMenuItem>
              ) : shortsError ? (
                <DropdownMenuItem disabled>Unable to load shorts.</DropdownMenuItem>
              ) : (
                activeShorts.map((short, index) => (
                  <DropdownMenuItem
                    key={short.id}
                    onClick={() => {
                      if (short.id === clipId) return
                      if (!isDirty) {
                        navigate({ to: "/app/videos/$videoId/clips/$clipId/edit", params: { videoId, clipId: short.id } })
                      } else {
                        setSwitchTarget(short.id)
                      }
                    }}
                    className="min-w-0"
                  >
                    {sourceMedia?.url && <ShortPreview src={sourceMedia.url} startSeconds={short.startSeconds} />}
                    <span className="min-w-0 flex-1 truncate">{short.title}</span>
                    <span className="text-muted-foreground">#{index + 1}</span>
                    {short.id === clipId && <Check data-icon="inline-end" aria-hidden="true" />}
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="app-editor-command-actions">
          <Button disabled={!canSave} onClick={() => void saveStyle("clip")}>
            {saving === "clip" ? (
              <Spinner data-icon="inline-start" />
            ) : (
              <Save data-icon="inline-start" aria-hidden="true" />
            )}
            Save
          </Button>
          <TikTokConnectDialog
            videoId={videoId}
            clipId={clipId}
            clipTitle={editor.clip.title}
            disabled={
              isDirty ||
              editor.clip.renderStatus !== "ready" ||
              editor.clip.renderedRevision !== editor.clip.renderRevision
            }
          />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger render={<span className="inline-flex" />}>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Download short"
                  disabled={!canDownload}
                  onClick={async () => {
                    if (!canDownload) return
                    const draftVersion = draftVersionRef.current
                    setDownloading(true)
                    try {
                      const media = await videoClient.clips.getMediaUrl({ videoId, clipId, purpose: "download" })
                      if (!dirtyRef.current && draftVersionRef.current === draftVersion) window.location.assign(media.url)
                    } catch {
                      setError("The download link is temporarily unavailable. Try again.")
                    } finally {
                      setDownloading(false)
                    }
                  }}
                >
                  {downloading ? <Spinner data-icon /> : <Download data-icon aria-hidden="true" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Download</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </section>

      <Dialog
        open={switchTarget !== null}
        onOpenChange={(open) => {
          if (!open && saving !== "clip") setSwitchTarget(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save changes before switching?</DialogTitle>
            <DialogDescription>
              This short has unsaved changes. Save them before opening another short, or discard the draft.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={saving === "clip"} onClick={() => setSwitchTarget(null)}>
              Stay here
            </Button>
            <Button variant="ghost" disabled={Boolean(saving)} onClick={() => {
              const target = switchTarget
              if (!target) return
              setSwitchTarget(null)
              navigate({ to: "/app/videos/$videoId/clips/$clipId/edit", params: { videoId, clipId: target } })
            }}>
              Discard and switch
            </Button>
            <Button disabled={!canSave} onClick={() => void saveStyle("clip")}>
              {saving === "clip" && <Spinner data-icon="inline-start" />}
              Save and switch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {error && (
        <Alert variant="destructive" className="app-page-alert">
          <AlertCircle aria-hidden="true" />
          <AlertTitle>Unable to update subtitles</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {editor.clip.renderStatus === "failed" && editor.clip.errorMessage && (
        <Alert variant="destructive">
          <AlertCircle aria-hidden="true" />
          <AlertTitle>The last render failed</AlertTitle>
          <AlertDescription>
            {editor.clip.errorMessage} Save this style to retry rendering.
          </AlertDescription>
        </Alert>
      )}

      <ResizablePanelGroup orientation="vertical" className="app-editor-layout">
        <ResizablePanel
          id="editor-columns-row"
          defaultSize="70%"
          minSize="280px"
          className="app-editor-columns-row"
        >
          <ResizablePanelGroup
            orientation="horizontal"
            className="app-editor-columns"
          >
            <ResizablePanel
              id="editor-media-panel"
              defaultSize="25%"
              minSize="18%"
              className="app-editor-media-panel"
            >
              {activeWorkspace === "reframing" ? (
                <Card className="app-editor-controls-card">
                  <CardHeader><CardTitle>Reframing</CardTitle><CardDescription>Adjust the source crop for each scene. Your short preview updates immediately.</CardDescription></CardHeader>
                  <CardContent className="flex flex-col gap-5">
                    <FieldGroup>
                      <Field><FieldLabel>Export format</FieldLabel><ToggleGroup value={[selectedFormat]} variant="outline" spacing={2} aria-label="Short format" onValueChange={value => {
                        const format = value[0] as ClipFormat | undefined
                        if (!format || format === selectedFormat) return
                        draftVersionRef.current += 1
                        dirtyRef.current = draftIsDirty({ format })
                        setSelectedFormat(format)
                      }}><ToggleGroupItem value="9:16" aria-label="Vertical 9 by 16"><RectangleVertical aria-hidden="true" />9:16</ToggleGroupItem><ToggleGroupItem value="16:9" aria-label="Horizontal 16 by 9"><RectangleHorizontal aria-hidden="true" />16:9</ToggleGroupItem></ToggleGroup></Field>
                      <Field>
                        <FieldLabel>Export resolution</FieldLabel>
                        <ToggleGroup
                          value={[String(selectedExportResolution)]}
                          variant="outline"
                          spacing={2}
                          aria-label="Export resolution"
                          disabled={Boolean(saving) || renderSaveBlocked}
                          onValueChange={(values) => {
                            const resolution = ([1080, 1440, 2160] as const).find(
                              (option) => String(option) === values[0]
                            )
                            if (!resolution || resolution === selectedExportResolution || !editor.allowedExportResolutions.includes(resolution)) return
                            draftVersionRef.current += 1
                            dirtyRef.current = draftIsDirty({ exportResolution: resolution })
                            setSelectedExportResolution(resolution)
                          }}
                        >
                          {([
                            { value: 1080, label: "1080p" },
                            { value: 1440, label: "2K" },
                            { value: 2160, label: "4K" },
                          ] as const).map((option) => (
                            <ToggleGroupItem
                              key={option.value}
                              value={String(option.value)}
                              disabled={!editor.allowedExportResolutions.includes(option.value)}
                              aria-label={`${option.label} export${editor.allowedExportResolutions.includes(option.value) ? "" : ", requires Pro or High volume"}`}
                            >
                              {option.label}
                            </ToggleGroupItem>
                          ))}
                        </ToggleGroup>
                        {!editor.allowedExportResolutions.includes(1440) && (
                          <FieldDescription>
                            2K and 4K exports are available on Pro and High volume.{" "}
                            <Link to="/app/billing">Upgrade plan</Link>
                          </FieldDescription>
                        )}
                      </Field>
                    </FieldGroup>
                    <p className="text-sm text-muted-foreground">Gameplay can be resized freely. A detected facecam keeps its original aspect ratio. Each scene saves its own framing.</p>
                  </CardContent>
                </Card>
              ) : activeWorkspace === "video" ? (
                  <EditorMediaLibrary
                    media={editor.mediaLibrary}
                    uploadingSounds={uploadingSounds}
                    uploadingImages={uploadingImages}
                    canAddSound={
                      activeSounds.length <
                      Math.min(4, editor.soundLimits.maxSounds)
                    }
                    canAddImage={
                      activeImages.length < editor.imageLimits.maxImages
                    }
                    onFiles={addMediaFiles}
                    onAssetSelect={(asset) => {
                      if (asset.kind === "image") addImageAsset(asset)
                      else addSoundAsset(asset)
                    }}
                  />
              ) : (
                <Card className="app-editor-controls-card app-caption-presets-panel">
                  <CardHeader>
                    <CardTitle>Subtitle appearance</CardTitle>
                    <CardDescription>
                      Choose the appearance for this short. Style and timeline
                      edits are saved for this short only.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="app-editor-controls">
                    <FieldGroup>
                      <Field>
                        <FieldLabel>Preset</FieldLabel>
                        <CaptionPresetCarousel
                          clipId={clipId}
                          sourceUrl={sourceMedia?.url ?? editor.sourceMedia.url}
                          sourceTime={cutRanges[0]?.startSeconds ?? editor.clip.startSeconds}
                          format={selectedFormat}
                          framingPlan={editor.clip.framingPlan}
                          facecamOverride={facecamOverride}
                          ariaLabel="Default subtitle presets"
                          presets={defaultPresetOptions}
                          selectedPreset={selectedSavedPresetId ?? selectedPreset}
                          disabled={!subtitlesEnabled}
                          appearance={appearance}
                          fontSize={selectedFontSize}
                          marginHorizontal={selectedMarginHorizontal}
                          position={selectedPosition}
                          positionSegments={captionPositionSegments}
                          cues={timedEditorCues}
                          onSelect={applyPreset}
                        />
                      </Field>
                      {savedPresetOptions.length > 0 && (
                        <Field>
                          <FieldLabel>Saved preset</FieldLabel>
                          <CaptionPresetCarousel
                            clipId={clipId}
                            sourceUrl={sourceMedia?.url ?? editor.sourceMedia.url}
                            sourceTime={cutRanges[0]?.startSeconds ?? editor.clip.startSeconds}
                            format={selectedFormat}
                            framingPlan={editor.clip.framingPlan}
                            facecamOverride={facecamOverride}
                            ariaLabel="Saved subtitle presets"
                            presets={savedPresetOptions}
                            selectedPreset={selectedSavedPresetId ?? selectedPreset}
                            disabled={!subtitlesEnabled}
                            appearance={appearance}
                            fontSize={selectedFontSize}
                            marginHorizontal={selectedMarginHorizontal}
                            position={selectedPosition}
                            positionSegments={captionPositionSegments}
                            cues={timedEditorCues}
                            onSelect={applyPreset}
                          />
                        </Field>
                      )}
                    </FieldGroup>
                  </CardContent>
                </Card>
              )}
            </ResizablePanel>

            <ResizableHandle withHandle />

            <ResizablePanel
              id="editor-video-panel"
              defaultSize="50%"
              minSize="32%"
              className="app-editor-preview-panel"
            >
              <Card className="app-editor-preview-card">
                <CardHeader>
                  <CardTitle>Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div
                    ref={previewFrameRef}
                    className={cn(
                      "app-editor-video-frame",
                      selectedFormat === "16:9" &&
                        "app-editor-video-frame-landscape",
                      showVerticalGuide && "app-editor-video-frame-guide-x",
                      showHorizontalGuide && "app-editor-video-frame-guide-y"
                    )}
                  >
                    {activeSounds.map((sound) => (
                      <audio
                        key={sound.id}
                        ref={(node) => {
                          if (node) soundAudioRefs.current.set(sound.id, node)
                          else soundAudioRefs.current.delete(sound.id)
                        }}
                        preload="metadata"
                        aria-hidden="true"
                        src={sound.media.url}
                      />
                    ))}
                    <video
                      ref={attachPreviewVideo}
                      style={
                        !sourceIsVisible ||
                        ((selectedFormat === "9:16" && editor.clip.framingPlan) || sceneFramingOverrides?.scenes.length)
                          ? { visibility: "hidden" }
                          : undefined
                      }
                      playsInline
                      muted={videoMuted || !sourceIsVisible}
                      preload="metadata"
                      aria-label={`Source preview for ${editor.clip.title}`}
                      src={sourceMedia?.url ?? editor.sourceMedia.url}
                      onLoadedMetadata={(event) =>
                        videoSync.onLoadedMetadata(event.currentTarget)
                      }
                      onTimeUpdate={(event) =>
                        videoSync.onTimeUpdate(event.currentTarget)
                      }
                      onLoadedData={(event) =>
                        videoSync.onLoadedData(event.currentTarget)
                      }
                      onSeeked={(event) =>
                        videoSync.onSeeked(event.currentTarget)
                      }
                      onPlay={videoSync.onPlay}
                      onPause={(event) =>
                        videoSync.onPause(event.currentTarget)
                      }
                      onSeeking={(event) =>
                        videoSync.onSeeking(event.currentTarget)
                      }
                      onError={videoSync.onPlaybackError}
                    >
                      <track kind="captions" />
                    </video>
                    {sourceIsVisible && ((selectedFormat === "9:16" && editor.clip.framingPlan) || Boolean(sceneFramingOverrides?.scenes.length)) && (
                      <FramingPreview
                        video={previewVideo}
                        plan={editor.clip.framingPlan}
                        sceneFramingOverrides={sceneFramingOverrides}
                        facecamOverride={facecamOverride}
                        format={selectedFormat}
                        sourceTime={sourceTimeAtTimelineTime(
                          cutRanges,
                          currentTime,
                          editor.clip.startSeconds
                        )}
                        className="absolute inset-0"
                      />
                    )}
                    {sourceIsVisible && sceneColorFilters.filter(filter => sourceTime >= filter.startSeconds && sourceTime < filter.endSeconds).map(filter => <div key={filter.startSeconds} aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ backgroundColor: filter.color, opacity: filter.opacity }} />)}
                    {sourceIsVisible && canEditFacecam && activeWorkspace !== "reframing" && !sceneFramingOverrides?.scenes.some(scene => sourceTime >= scene.startSeconds && sourceTime < scene.endSeconds) && (
                      <div
                        aria-label="Move facecam crop"
                        className="app-editor-facecam-control absolute cursor-grab touch-none active:cursor-grabbing"
                        style={{ left: "2.5%", top: "1%", width: "95%", height: "38%", zIndex: 1 }}
                        onPointerDown={startFacecamDrag}
                        onPointerMove={moveFacecamDrag}
                        onPointerUp={stopFacecamDrag}
                        onPointerCancel={stopFacecamDrag}
                      />
                    )}
                    {[...activeImages]
                      .sort(
                        (left, right) =>
                          left.layerIndex - right.layerIndex ||
                          left.id.localeCompare(right.id)
                      )
                      .filter(
                        (image) =>
                          currentTime >= image.timelineStartSeconds &&
                          currentTime < image.timelineEndSeconds
                      )
                      .sort(
                        (left, right) =>
                          left.trackIndex - right.trackIndex ||
                          left.id.localeCompare(right.id)
                      )
                      .map((image) => (
                        <div
                          key={image.id}
                          className={cn(
                            "app-editor-image-overlay",
                            selectedImageId === image.id &&
                              "app-editor-image-overlay-selected"
                          )}
                          style={{
                            left: `${image.x * 100}%`,
                            top: `${image.y * 100}%`,
                            width: `${image.width * 100}%`,
                            height: `${image.height * 100}%`,
                            opacity: image.opacity,
                            zIndex: image.layerIndex + 2,
                          }}
                          onPointerDown={(event) =>
                            startImageTransform(event, image, "move")
                          }
                        >
                          <img src={image.media.url} alt="" draggable={false} />
                          {selectedImageId === image.id && (
                            <div
                              className="app-editor-image-resize"
                              aria-label={`Resize ${image.filename}`}
                              onPointerDown={(event) =>
                                startImageTransform(event, image, "resize")
                              }
                            />
                          )}
                        </div>
                      ))}
                    {subtitlesEnabled &&
                      showPlacementPreview &&
                      previewStyle && (
                        <div
                          ref={previewTextRef}
                          className={cn(
                            "app-subtitle-preview app-subtitle-preview-playback",
                            `app-subtitle-preset-${selectedPreset}`,
                            dragging && "app-subtitle-preview-dragging"
                          )}
                          role="group"
                          tabIndex={0}
                          aria-label="Subtitle placement"
                          onKeyDown={movePositionWithKeyboard}
                          onPointerDown={startDragging}
                          onPointerMove={(event) => {
                            if (
                              event.currentTarget.hasPointerCapture(
                                event.pointerId
                              )
                            )
                              movePositionFromPointer(event)
                          }}
                          onPointerUp={stopDragging}
                          onPointerCancel={stopDragging}
                          style={subtitleVisualStyleForCue(activeCue)}
                        >
                          <CaptionPreview
                            cue={activeCue}
                            time={presentationTime}
                            preset={selectedPreset}
                              accentColor={appearance.accentColor}
                              textColor={appearance.textColor ?? previewStyle.color}
                            fallback=""
                          />
                        </div>
                      )}
                    {subtitlesEnabled && previewStyle && (
                      <div
                        ref={previewMeasurementRef}
                        className="app-subtitle-preview-measurements"
                        aria-hidden="true"
                      >
                        {displayCues.map((cue) => (
                          <div
                            key={`${cue.start}-${cue.end}`}
                            className={cn(
                              "app-subtitle-preview app-subtitle-preview-measurement",
                              `app-subtitle-preset-${selectedPreset}`
                            )}
                            style={{
                              textTransform: captionTextTransform,
                              fontFamily: appearance.fontFamily ? captionFontFamilies[appearance.fontFamily] : previewStyle.fontFamily,
                              fontWeight: fixedFontWeight ?? (appearance.bold === null ? previewStyle.fontWeight : appearance.bold ? 700 : 400),
                              fontStyle: fixedFontWeight === undefined && appearance.italic ? "italic" : "normal",
                              fontSynthesis: fixedFontWeight === undefined ? undefined : "none",
                              fontSize: `${(cue.fontSizePx ?? selectedFontSize) / 10.8}cqw`,
                              color: appearance.textColor ?? previewStyle.color,
                              backgroundColor: previewStyle.backgroundColor,
                              WebkitTextStroke: `${previewStyle.strokeWidth / 10.8}cqw ${previewStyle.strokeColor}`,
                              textShadow:
                                previewStyle.shadowWidth > 0
                                  ? `0 ${previewStyle.shadowWidth / 10.8}cqw ${previewStyle.shadowWidth / 10.8}cqw ${previewStyle.strokeColor}`
                                  : undefined,
                              width: "fit-content",
                              maxWidth: `${subtitleAvailableWidthPercent}%`,
                            }}
                          >
                            <CaptionPreview
                              cue={cue}
                              time={cue.start}
                              preset={selectedPreset}
                              accentColor={appearance.accentColor}
                              textColor={appearance.textColor ?? previewStyle.color}
                              fallback={cue.text}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="app-editor-preview-controls-row">
                    <div className="app-editor-preview-controls-inner">
                  <div
                    className="app-editor-video-controls"
                    aria-label="Short video controls"
                  >
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      onClick={videoSync.togglePlayback}
                      aria-label={videoPaused ? "Play short" : "Pause short"}
                    >
                      {videoPaused ? (
                        <Play data-icon="inline-start" aria-hidden="true" />
                      ) : (
                        <Pause data-icon="inline-start" aria-hidden="true" />
                      )}
                    </Button>
                    <Slider
                      value={[currentTime]}
                      min={0}
                      max={clipDuration || 1}
                      step={0.1}
                      aria-label="Short playback position"
                      onValueChange={(value) =>
                        videoSync.seek(
                          typeof value === "number" ? value : (value[0] ?? 0)
                        )
                      }
                    />
                    <span className="app-editor-video-time" aria-live="off">
                      {formatTime(currentTime)} / {formatTime(clipDuration)}
                    </span>
                    <Popover>
                      <PopoverTrigger
                        render={
                          <Button
                            type="button"
                            variant="secondary"
                            size="icon"
                            aria-label="Adjust preview volume"
                          />
                        }
                      >
                        {videoMuted ? (
                          <VolumeX
                            data-icon="inline-start"
                            aria-hidden="true"
                          />
                        ) : (
                          <Volume2
                            data-icon="inline-start"
                            aria-hidden="true"
                          />
                        )}
                      </PopoverTrigger>
                      <PopoverContent aria-label="Preview volume">
                        <PopoverTitle>Preview volume</PopoverTitle>
                        <span className="text-muted-foreground">
                          {videoVolume}%
                        </span>
                        <Slider
                          value={[videoVolume]}
                          min={0}
                          max={100}
                          step={1}
                          aria-label="Preview volume"
                          onValueChange={(value) => {
                            const nextVolume =
                              typeof value === "number"
                                ? value
                                : (value[0] ?? 100)
                            setVideoVolume(nextVolume)
                            setVideoMuted(nextVolume === 0)
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <Field className="app-editor-timeline-zoom">
                    <div className="flex min-w-0 items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Zoom out timeline"
                        disabled={timelineZoom <= 1}
                        onClick={() => setTimelineZoom((zoom) => Math.max(1, zoom - 0.25))}
                      >
                        <ZoomOut data-icon aria-hidden="true" />
                      </Button>
                      <Slider
                        id="timeline-zoom"
                        className="min-w-0 flex-1"
                        value={[timelineZoom]}
                        min={1}
                        max={8}
                        step={0.25}
                        aria-label="Timeline zoom"
                        aria-valuetext={`${Math.round(timelineZoom * 100)}%`}
                        onValueChange={(value) =>
                          setTimelineZoom(typeof value === "number" ? value : (value[0] ?? 1))
                        }
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Zoom in timeline"
                        disabled={timelineZoom >= 8}
                        onClick={() => setTimelineZoom((zoom) => Math.min(8, zoom + 0.25))}
                      >
                        <ZoomIn data-icon aria-hidden="true" />
                      </Button>
                    </div>
                  </Field>
                    </div>
                  </div>
                  {timelineHost &&
                    createPortal(
                      <div className="app-editor-timeline-content">
                        {!sceneBoundariesApplied && <div className="flex min-h-12 flex-wrap items-center gap-2" aria-label="Scene detection">
                          {sceneDetectionControl}
                          {editor.clip.sceneStatus === "ready" && editor.clip.sourceScenes.length > 0 && <>
                            {editor.clip.sourceScenes.length > 1 && <Button type="button" variant="outline" size="sm" disabled={!canApplyScenes} onClick={() => {
                              const next = sceneCutRanges
                              if (next.length > editor.editLimits.maxRanges) { setSceneRequestError(`Applying these scenes would exceed the limit of ${editor.editLimits.maxRanges} retained ranges.`); return }
                              setSceneRequestError(null)
                              updateCutRanges(next)
                              void saveStyle("clip", next)
                            }}>
                              {saving === "clip" && <Spinner data-icon="inline-start" />}
                              {saving === "clip" ? "Saving scenes…" : "Apply scene boundaries"}
                            </Button>}
                          </>}
                        </div>}
                        {(sceneRequestError || editor.clip.sceneStatus === "failed") && <Alert variant="destructive"><AlertTitle>Scene detection unavailable</AlertTitle><AlertDescription>{sceneRequestError ?? editor.clip.sceneError ?? "Scene detection failed. You can retry it."}</AlertDescription></Alert>}
                        <ClipTimeline
                          key={clipId}
                          zoom={timelineZoom}
                          filters={sceneColorFilters}
                          onFilterSelect={filter => {
                            videoRef.current?.pause()
                            videoSync.seek(timelineTimeAtSourceTime(cutRanges, filter.startSeconds))
                            window.dispatchEvent(new CustomEvent("clip-editor-section", { detail: "video" }))
                            setSelectedSoundId(null)
                            setSelectedImageId(null)
                          }}
                          scenes={effectiveEditorScenes(editor.clip.sourceScenes, cutRanges)}
                          ranges={cutRanges}
                          originalStart={editor.clip.startSeconds}
                          duration={clipDuration}
                          currentTime={currentTime}
                          selectedRange={selectedRange}
                          captionCues={timedEditorCues}
                          selectedCaptionIdentity={selectedCaptionIdentity}
                          splitActive={splitActive}
                          canUndo={undoHistory.length > 0}
                          maxRanges={editor.editLimits.maxRanges}
                          sounds={activeSounds}
                          selectedSoundId={selectedSoundId}
                          images={activeImages}
                          selectedImageId={selectedImageId}
                          sourceWaveform={editor.sourceWaveform}
                          onSplitActiveChange={setSplitActive}
                          onSeek={videoSync.seek}
                          onSplit={(time) =>
                            updateCutRanges(
                              splitRange(
                                cutRanges,
                                time,
                                editor.editLimits.minRangeSeconds,
                                editor.clip.startSeconds
                              )
                            )
                          }
                          onSelect={(index) => {
                            selectVideoRange(index)
                            videoRef.current?.pause()
                            window.dispatchEvent(new CustomEvent("clip-editor-section", { detail: "reframing" }))
                          }}
                          onCaptionSelect={(identity) => {
                            setSelectedCaptionIdentity(identity)
                            setCaptionTab("text")
                            setCaptionFocusRequest({ cueKey: identity })
                            window.dispatchEvent(new CustomEvent("clip-editor-section", { detail: "captions" }))
                            const cue = timedEditorCues.find((candidate) =>
                              `${candidate.startWordIndex}:${candidate.endWordIndex}` === identity)
                            if (cue) {
                              videoRef.current?.pause()
                              videoSync.seek(cue.start)
                            }
                          }}
                          onCaptionTimingChange={(entry) => {
                            const next = [
                              ...captionTimingOverrides.filter(
                                (candidate) =>
                                  candidate.startWordIndex !== entry.startWordIndex ||
                                  candidate.endWordIndex !== entry.endWordIndex
                              ),
                              entry,
                            ].sort((left, right) => left.startWordIndex - right.startWordIndex)
                            draftVersionRef.current += 1
                            dirtyRef.current = draftIsDirty({ captionTimingOverrides: next })
                            setCaptionTimingOverrides(next)
                          }}
                          onCaptionDelete={(cue) => {
                            const entry = {
                              startWordIndex: cue.startWordIndex,
                              endWordIndex: cue.endWordIndex,
                              start: cue.start,
                              end: cue.end,
                              deleted: true,
                            }
                            const next = [
                              ...captionTimingOverrides.filter(
                                (candidate) =>
                                  candidate.startWordIndex !== entry.startWordIndex ||
                                  candidate.endWordIndex !== entry.endWordIndex
                              ),
                              entry,
                            ].sort((left, right) => left.startWordIndex - right.startWordIndex)
                            draftVersionRef.current += 1
                            dirtyRef.current = draftIsDirty({ captionTimingOverrides: next })
                            setCaptionTimingOverrides(next)
                            setSelectedCaptionIdentity(null)
                          }}
                          onTrim={(index, edge, trimSourceTime) =>
                            updateCutRanges(
                              trimRange(
                                cutRanges,
                                index,
                                edge,
                                trimSourceTime,
                                editor.editLimits.minRangeSeconds
                              )
                            )
                          }
                          onUndo={undo}
                          onSoundSelect={selectTimelineSound}
                          onSoundMove={moveSoundToTrack}
                          onSoundTrim={trimSoundToTrack}
                          onSoundSplit={splitSoundOnTrack}
                          onImageSelect={selectTimelineImage}
                          onImageRangeChange={moveImageToTrack}
                          onImageSplit={splitImageOnTrack}
                        />
                      </div>,
                      timelineHost
                    )}
                </CardContent>
              </Card>
            </ResizablePanel>

            <ResizableHandle withHandle />

            <ResizablePanel
              id="editor-captions-panel"
              defaultSize="25%"
              minSize="18%"
              className="app-editor-captions-panel"
            >
              {activeWorkspace === "reframing" ? (
                <ReframingEditor sceneDetectionControl={sceneDetectionControl} sceneDetectionError={sceneRequestError ?? (editor.clip.sceneStatus === "failed" ? editor.clip.sceneError : null)} video={previewVideo} clip={{ ...editor.clip, scenes: effectiveEditorScenes(editor.clip.sourceScenes, cutRanges) }} sourceTime={sourceTime} format={selectedFormat} value={sceneFramingOverrides} disabled={saving !== null || renderSaveBlocked} onSeek={time => videoSync.seek(timelineTimeAtSourceTime(cutRanges, editor.clip.startSeconds + time))} onChange={(next, captionPlacement) => {
                  let nextPositions = captionPositionSegments
                  if (captionPlacement) {
                    try {
                      for (const placement of Array.isArray(captionPlacement) ? captionPlacement : [captionPlacement]) {
                        nextPositions = positionCaptionRange(
                          nextPositions,
                          timelineTimeAtSourceTime(cutRanges, placement.startSeconds),
                          timelineTimeAtSourceTime(cutRanges, placement.endSeconds),
                          placement.position
                        )
                      }
                    } catch (cause) {
                      setError(cause instanceof Error ? cause.message : "Could not place subtitles for this layout.")
                      return false
                    }
                  }
                  draftVersionRef.current += 1
                  dirtyRef.current = draftIsDirty({ sceneFramingOverrides: next, captionPositionSegments: nextPositions })
                  setSceneFramingOverrides(next)
                  if (captionPlacement) setCaptionPositionSegments(nextPositions)
                  return true
                }} />
              ) : activeWorkspace !== "captions" ? (
                <Tabs key="video-settings" defaultValue="filters" className="min-h-full">
                  <Card className="app-editor-controls-card">
                    <CardHeader>
                      <CardTitle>Video</CardTitle>
                      <TabsList className="mt-2 w-full" aria-label="Video settings">
                        <TabsTrigger value="filters">Filters</TabsTrigger>
                      </TabsList>
                    </CardHeader>
                    <Separator />
                    <CardContent className="app-editor-controls">
                      {canEditFacecam && (
                        <FieldGroup>
                          <Field>
                            <FieldLabel htmlFor="facecam-zoom">
                              Facecam zoom · {facecamZoom}%
                            </FieldLabel>
                            <Slider
                              id="facecam-zoom"
                              value={[facecamZoom]}
                              min={minimumFacecamZoom}
                              max={300}
                              step={1}
                              aria-label="Facecam zoom"
                              onValueChange={(value) =>
                                setFacecamZoom(
                                  typeof value === "number" ? value : (value[0] ?? 100)
                                )
                              }
                            />
                            <FieldDescription>
                              Drag the facecam in the preview to reposition it inside its frame.
                            </FieldDescription>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={facecamOverride === null}
                              onClick={() => updateFacecamOverride(null)}
                            >
                              Reset facecam crop
                            </Button>
                          </Field>
                        </FieldGroup>
                      )}
                      <TabsContent value="filters" className="w-full">
                        <SceneFilters scenes={effectiveEditorScenes(editor.clip.sourceScenes, cutRanges)} sourceTime={sourceTime} value={sceneColorFilters} disabled={saving !== null || renderSaveBlocked} onChange={next => {
                          setSceneColorFilters(next)
                          dirtyRef.current = draftIsDirty({ sceneColorFilters: next })
                          draftVersionRef.current += 1
                        }} />
                      </TabsContent>
                      {mediaInspector}
                    </CardContent>
                  </Card>
                </Tabs>
              ) : (
                <Tabs key="caption-settings" value={captionTab} onValueChange={setCaptionTab} className="min-h-full">
                  <Card className="app-editor-controls-card">
                    <CardHeader>
                      <div className="flex items-center justify-between gap-2">
                        <CardTitle>Captions</CardTitle>
                        <Switch
                          id="caption-visibility"
                          aria-label="Enable subtitles"
                          checked={subtitlesEnabled}
                          onCheckedChange={(next) => {
                            draftVersionRef.current += 1
                            dirtyRef.current = draftIsDirty({ subtitlesEnabled: next })
                            setSubtitlesEnabled(next)
                          }}
                        />
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <TabsList className="min-w-0 flex-1">
                          <TabsTrigger value="text">Text</TabsTrigger>
                          <TabsTrigger value="style">Style</TabsTrigger>
                        </TabsList>
                        <DropdownMenu>
                          <DropdownMenuTrigger render={
                            <Button type="button" variant="ghost" size="icon-sm" aria-label="Caption actions" />
                          }>
                            <Ellipsis data-icon aria-hidden="true" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuGroup>
                              <DropdownMenuItem disabled={saving !== null || !captionsAreDirty} onClick={resetCaptionEdits}>
                                <RotateCcw data-icon="inline-start" aria-hidden="true" />
                                Reset caption
                              </DropdownMenuItem>
                            </DropdownMenuGroup>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardHeader>
                    <Separator />
                    <TabsContent value="text">
                      <CaptionsEditor
                        embedded
                        focusRequest={captionFocusRequest}
                        cues={editorCues}
                        activeWord={activeCaptionWord}
                        disabled={saving !== null}
                        onChange={updateCaption}
                        onSeek={videoSync.seek}
                        hiddenCueKeys={new Set(captionTimingOverrides
                          .filter((entry) => entry.deleted)
                          .map((entry) => `${entry.startWordIndex}:${entry.endWordIndex}`))}
                        onToggleVisibility={(cue) => {
                          const existing = captionTimingOverrides.find((entry) =>
                            entry.startWordIndex === cue.startWordIndex &&
                            entry.endWordIndex === cue.endWordIndex)
                          const entry = {
                            startWordIndex: cue.startWordIndex,
                            endWordIndex: cue.endWordIndex,
                            start: existing?.start ?? cue.start,
                            end: existing?.end ?? cue.end,
                            deleted: !existing?.deleted,
                          }
                          const restoredOriginal = !entry.deleted &&
                            entry.start === cue.start && entry.end === cue.end &&
                            !editor.clip.captionTimingOverrides.some((saved) =>
                              saved.startWordIndex === cue.startWordIndex &&
                              saved.endWordIndex === cue.endWordIndex)
                          const next = [
                            ...captionTimingOverrides.filter((candidate) =>
                              candidate.startWordIndex !== cue.startWordIndex ||
                              candidate.endWordIndex !== cue.endWordIndex),
                            ...(restoredOriginal ? [] : [entry]),
                          ].sort((left, right) => left.startWordIndex - right.startWordIndex)
                          draftVersionRef.current += 1
                          dirtyRef.current = draftIsDirty({ captionTimingOverrides: next })
                          setCaptionTimingOverrides(next)
                          setSelectedCaptionIdentity(null)
                        }}
                      />
                    </TabsContent>
                    <TabsContent value="style">
                      <CardContent className="app-editor-controls app-caption-style-panel">
                        {!subtitlesEnabled && (
                          <p role="status" className="text-sm text-muted-foreground">
                            Subtitles are off for this short. Save to create an
                            export without subtitles.
                          </p>
                        )}
                        {subtitlesEnabled && (
                          <FieldGroup className="app-caption-style-groups">
                            <FieldSet className="app-caption-style-section">
                              <FieldLegend variant="label">Typography</FieldLegend>
                              <FieldGroup className="gap-4">
                                <Field className="gap-2">
                                  <FieldLabel htmlFor="caption-font">Font</FieldLabel>
                                  <Select
                                    items={[
                                      { value: "preset", label: "Preset default" },
                                      ...Object.keys(captionFontFamilies).map((font) => ({ value: font, label: font })),
                                    ]}
                                    value={appearance.fontFamily ?? "preset"}
                                    onValueChange={(value) => {
                                      if (value === "preset") {
                                        updateAppearance({ fontFamily: null })
                                        return
                                      }
                                      const fontFamily = Object.keys(captionFontFamilies).find(
                                        (font): font is keyof typeof captionFontFamilies => font === value
                                      )
                                      if (fontFamily) updateAppearance({ fontFamily })
                                    }}
                                  >
                                    <SelectTrigger id="caption-font" className="w-full"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectGroup>
                                        <SelectItem value="preset">Preset default</SelectItem>
                                        {Object.keys(captionFontFamilies).map((font) => (
                                          <SelectItem key={font} value={font}>{font}</SelectItem>
                                        ))}
                                      </SelectGroup>
                                    </SelectContent>
                                  </Select>
                                </Field>
                                <Field className="gap-2">
                                  <div className="flex items-center justify-between gap-2">
                                    <FieldLabel id="caption-size-label">Font size</FieldLabel>
                                    <span className="text-sm tabular-nums text-muted-foreground">{selectedFontSize} px</span>
                                  </div>
                                  <Slider
                                    value={[selectedFontSize]}
                                    min={editor.fontSizeLimits.min}
                                    max={editor.fontSizeLimits.max}
                                    step={editor.fontSizeLimits.step}
                                    aria-labelledby="caption-size-label"
                                    onValueChange={(value) => {
                                      const next = typeof value === "number" ? value : value[0]
                                      draftVersionRef.current += 1
                                      dirtyRef.current = draftIsDirty({ fontSizePx: next })
                                      setSelectedFontSize(next)
                                    }}
                                  />
                                </Field>
                                <div className="app-caption-emphasis-grid">
                                  <Field orientation="horizontal" className="gap-2">
                                    <FieldLabel htmlFor="caption-bold">Bold</FieldLabel>
                                    <Switch
                                      id="caption-bold"
                                      checked={fixedFontWeight === undefined ? appearance.bold ?? ((previewStyle?.fontWeight ?? 700) >= 700) : fixedFontWeight >= 700}
                                      disabled={fixedFontWeight !== undefined}
                                      onCheckedChange={(checked) => updateAppearance({ bold: checked })}
                                    />
                                    <Button
                                      type="button" variant="ghost" size="icon-sm"
                                      aria-label="Reset bold to preset" title="Reset bold to preset"
                                      disabled={fixedFontWeight !== undefined || appearance.bold === null}
                                      onClick={() => updateAppearance({ bold: null })}
                                    ><RotateCcw /></Button>
                                  </Field>
                                  <Field orientation="horizontal" className="gap-2">
                                    <FieldLabel htmlFor="caption-italic">Italic</FieldLabel>
                                    <Switch
                                      id="caption-italic" checked={fixedFontWeight === undefined && (appearance.italic ?? false)}
                                      disabled={fixedFontWeight !== undefined}
                                      onCheckedChange={(checked) => updateAppearance({ italic: checked })}
                                    />
                                    <Button
                                      type="button" variant="ghost" size="icon-sm"
                                      aria-label="Reset italic to preset" title="Reset italic to preset"
                                      disabled={fixedFontWeight !== undefined || appearance.italic === null}
                                      onClick={() => updateAppearance({ italic: null })}
                                    ><RotateCcw /></Button>
                                  </Field>
                                </div>
                                <Field className="gap-2">
                                  <FieldLabel id="caption-case-label">Case</FieldLabel>
                                  <ButtonGroup aria-labelledby="caption-case-label" className="app-caption-case-buttons w-full">
                                    <Button type="button" size="xs" className="min-h-8 flex-1" variant={appearance.textCase === null ? "default" : "outline"} aria-pressed={appearance.textCase === null} aria-label="Use preset default case" onClick={() => updateAppearance({ textCase: null })}>Default</Button>
                                    <Button type="button" size="xs" className="min-h-8 flex-1" variant={appearance.textCase === "lowercase" ? "default" : "outline"} aria-pressed={appearance.textCase === "lowercase"} onClick={() => updateAppearance({ textCase: "lowercase" })}>lowercase</Button>
                                    <Button type="button" size="xs" className="min-h-8 flex-1" variant={appearance.textCase === "uppercase" ? "default" : "outline"} aria-pressed={appearance.textCase === "uppercase"} onClick={() => updateAppearance({ textCase: "uppercase" })}>UPPERCASE</Button>
                                  </ButtonGroup>
                                </Field>
                              </FieldGroup>
                            </FieldSet>
                            <Separator />
                            <FieldSet className="app-caption-style-section">
                              <FieldLegend variant="label">Colors</FieldLegend>
                              <FieldGroup className="app-caption-color-grid">
                                {([
                                  { key: "textColor", label: "Text color", value: appearance.textColor ?? previewStyle?.color ?? "#FFFFFF" },
                                  { key: "accentColor", label: "Accent color", value: appearance.accentColor ?? defaultAccentColor },
                                ] as const).map(({ key, label, value }) => (
                                  <Field key={key} className="gap-2">
                                    <FieldLabel htmlFor={`caption-${key}`}>{label}</FieldLabel>
                                    <div className="app-caption-color-control">
                                      <Input
                                        id={`caption-${key}`} type="color" className="app-caption-color-swatch"
                                        value={value}
                                        onChange={(event) => updateAppearance({ [key]: event.target.value })}
                                      />
                                      <span className="text-sm tabular-nums text-muted-foreground">{value.toUpperCase()}</span>
                                      <Button
                                        type="button" variant="ghost" size="icon-sm"
                                        aria-label={`Reset ${label.toLowerCase()} to preset`}
                                        title={`Reset ${label.toLowerCase()} to preset`}
                                        disabled={appearance[key] === null}
                                        onClick={() => updateAppearance({ [key]: null })}
                                      ><RotateCcw /></Button>
                                    </div>
                                  </Field>
                                ))}
                              </FieldGroup>
                            </FieldSet>
                            <Separator />
                            <FieldSet className="app-caption-style-section">
                              <FieldLegend variant="label">Display</FieldLegend>
                              <FieldGroup>
                                <Field orientation="horizontal" className="gap-3">
                                  <FieldLabel htmlFor="caption-max-words">Maximum words on screen</FieldLabel>
                                  <Input
                                    id="caption-max-words" className="app-caption-word-limit"
                                    type="number" min={1} max={20} step={1}
                                    value={appearance.maxWords ?? SUBTITLE_DEFAULT_MAX_WORDS}
                                    onChange={(event) => {
                                      if (event.target.value === "") {
                                        updateAppearance({ maxWords: SUBTITLE_DEFAULT_MAX_WORDS })
                                        return
                                      }
                                      const value = event.target.valueAsNumber
                                      if (Number.isFinite(value)) updateAppearance({
                                        maxWords: Math.min(20, Math.max(1, Math.trunc(value))),
                                      })
                                    }}
                                  />
                                </Field>
                              </FieldGroup>
                            </FieldSet>
                          </FieldGroup>
                        )}
                        <Button type="button" variant="outline" size="sm" onClick={() => { setPresetName(""); setPresetError(null); setSavePresetOpen(true) }}>
                          <Save data-icon="inline-start" aria-hidden="true" />Save preset
                        </Button>
                      </CardContent>
                    </TabsContent>
                  </Card>
                </Tabs>
              )}
            </ResizablePanel>
          </ResizablePanelGroup>
        </ResizablePanel>

        <ResizableHandle withHandle />

        <ResizablePanel
          id="editor-timeline-panel"
          defaultSize="30%"
          minSize="156px"
          className="app-editor-timeline-panel"
        >
          <section
            ref={setTimelineHost}
            className="app-editor-timeline-row"
            aria-label="Editing timeline"
          />
        </ResizablePanel>
      </ResizablePanelGroup>
    </main>
  )
}
